import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TableSortLabel, Chip, IconButton, TextField, FormControlLabel, Switch, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup, InputAdornment, Autocomplete, Tooltip as MuiTooltip } from '@mui/material';
import {
  Cloud as SalesforceIcon,
  ViewKanban as L2Icon,
  Engineering as L3Icon,
  Build as SreIcon,
  ArrowForward as ArrowForwardIcon,
  Layers as PlatformIcon,
  Groups as TopCustomersIcon,
  OpenInNew as OpenInNewIcon,
  ChatBubbleOutline as CommentIcon,
  Search as SearchIcon,
  CloudDownload as CloudDownloadIcon,
  Info as InfoIcon,
  Link as LinkIcon,
  Add as AddIcon,
  WarningAmber as MajorIncidentIcon
} from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { authGet, authPost, getServiceNowTickets } from '../../services/apiService';
import { hasOperationalExcellenceAccess, hasPermission } from '../../services/authService';
import TopCustomersPage from './top-customers/TopCustomersPage';
import OpenProductionBugsView from './OpenProductionBugsView';
import OpenL2IncidentsView from './OpenL2IncidentsView';
import SreOpenView from './SreOpenView';
import PlatformInvestigationTeamTab, { PLATFORM_INVESTIGATION_TEAMS } from '../platform-investigation/PlatformInvestigationTeamTab';
import TicketJourneyDialog, { CaseDetailsDialog } from './TicketJourneyDialog';
import AdoItemDetailsDialog from './AdoItemDetailsDialog';
import SnowTicketDetailsDialog, { SNOW_NUMBER } from './SnowTicketDetailsDialog';
import {
  AssigneeCell,
  CaseIdLink,
  displayAssigneeName,
  ownerFromAssignedTo,
  isUnassigned,
  absoluteHttpUrl
} from './assigneeDisplay';
import { formatTicketAge, formatSfTableDate, tableHeaderCellSx } from './sfTableFormatting';
import { useAdoCommentComposer } from './useAdoCommentComposer';
import { MultiCheckSelect, ALL_FILTER, isAllFilter, filterAllows } from './MultiCheckSelect';
import {
  DEEP_GREEN,
  MUTED,
  INK,
  NEUTRAL_SURFACE,
  L1_ACCENT,
  L1_BADGE_BG,
  L2_ACCENT,
  L2_BADGE_BG,
  INCIDENT_ACCENT,
  WARN_ORANGE,
  TOP_CUSTOMERS_ACCENT,
  TOP_CUSTOMERS_ACCENT_BG,
  MAJOR_INCIDENT_ACCENT,
  MAJOR_INCIDENT_BG
} from './colorTokens';

const GREEN_LIGHT = '#2C4A3A';
const L3_ACCENT = '#1A2E22';
const L3_BADGE_BG = '#eef3f0';
const INCIDENT_BADGE_BG = '#fdecea';
const WARN_ORANGE_BG = '#fbe9dd';

// Only the L1 tile opens a tab (`tab`). The other counts come from ADO and do not
// match any Salesforce-case list, so those tiles are display-only.
const TICKET_STAT_TILES = [
  { key: 'topCustomers', label: 'Top Customers', icon: TopCustomersIcon, color: TOP_CUSTOMERS_ACCENT, bg: TOP_CUSTOMERS_ACCENT_BG, dataSource: 'Data Lake, New/Open/WIP cases of top customers' },
  { key: 'l1Open', label: 'L1 Open', tab: 'l1', icon: SalesforceIcon, color: WARN_ORANGE, bg: WARN_ORANGE_BG, dataSource: 'Data Lake' },
  { key: 'l2Open', label: 'L2 Open', icon: L2Icon, color: '#2E7D32', bg: L3_BADGE_BG, dataSource: 'ADO TSI Investigations, open', valueLabel: 'Open' },
  { key: 'sreOpen', label: 'Site Reliability Engineering/DevOps', icon: SreIcon, color: L1_ACCENT, bg: L1_BADGE_BG, dataSource: 'ServiceNow tickets linked to L1 cases, open' },
  { key: 'l3Swat', label: 'L3 SWAT', icon: L3Icon, color: '#b71c1c', bg: INCIDENT_BADGE_BG, dataSource: 'ADO Production Bugs, open (excl. Resolved)' },
  { key: 'l3Platform', label: 'L3 Platform Services', icon: PlatformIcon, color: '#0d47a1', bg: L2_BADGE_BG, dataSource: 'ADO Platform Investigation, open' }
];

const UNIQUE_OPEN_ROWS = [
  { key: 'salesforce', label: 'Salesforce', color: L1_ACCENT, bg: L1_BADGE_BG },
  { key: 'l2ado', label: 'Linked to L2', color: L2_ACCENT, bg: L2_BADGE_BG },
  { key: 'l3ado', label: 'Linked to L3', color: L3_ACCENT, bg: L3_BADGE_BG },
  { key: 'snow', label: 'Linked to ServiceNow', color: INCIDENT_ACCENT, bg: INCIDENT_BADGE_BG }
];

/** Status text (Salesforce, ADO or ServiceNow) that says the ticket is blocked. */
const isBlockedStatus = (status) => /\bblocked\b/i.test(String(status || ''));

const isSnowLinkType = (linkType) => linkType === 'Incident' || linkType === 'Service';

// The ticket a multi-ticket cell shows: the one linked directly from the Salesforce case, else the first.
const pickTicketId = (ids, linkedId) => (
  (linkedId && ids.find((id) => String(id) === String(linkedId))) || ids[0] || ''
);
const linkedAdoIdOf = (ticket) => String((ticket && ticket.linkedAdo && ticket.linkedAdo.id) || '');
const l2IdsOf = (ticket) => (ticket.l2Ids && ticket.l2Ids.length ? ticket.l2Ids : [ticket.l2Id].filter(Boolean));

const LINKED_FROM_CASE_TIP = 'Linked directly from the Salesforce case';
const LinkedMark = () => (
  <MuiTooltip title={LINKED_FROM_CASE_TIP} arrow>
    <LinkIcon aria-label={LINKED_FROM_CASE_TIP} sx={{ fontSize: '0.875rem', color: 'text.secondary', flexShrink: 0 }} />
  </MuiTooltip>
);

/**
 * L2 / L3 ticket cell. Several tickets show one (the linked one, else the first) with a "+" whose tooltip lists the
 * rest; the Ticket Journey (click the row) shows them all. A link icon marks a ticket linked directly from the case.
 */
const MultiTicketCell = ({ ids, urlOf, linkedId, color, onOpen }) => {
  const shown = pickTicketId(ids, linkedId);
  const others = ids.filter((id) => String(id) !== String(shown));
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <CaseIdLink
        value={shown}
        href={urlOf(shown)}
        color={color}
        onClick={onOpen ? () => onOpen({ id: shown, url: urlOf(shown), color }) : undefined}
      />
      {shown && linkedId && String(shown) === String(linkedId) ? <LinkedMark /> : null}
      {others.length ? (
        <MuiTooltip title={`${others.length} more: ${others.join(', ')}`} arrow>
          <AddIcon aria-label={`${others.length} more tickets`} sx={{ fontSize: '0.875rem', color, flexShrink: 0 }} />
        </MuiTooltip>
      ) : null}
    </Box>
  );
};

const hasL2Link = (ticket) => Boolean(ticket && String(ticket.l2Id || '').trim());
const hasL3Link = (ticket) => Boolean(
  ticket
  && (
    (ticket.l3Ids && ticket.l3Ids.length)
    || ticket.location === 'L3'
  )
);
// An L2 ticket can come from the ticket linked on the case or from a Zendesk Support ID match, so these
// no longer require the case's own link to be an ADO link.
const isL2OnlyLink = (ticket) => (
  ticket
  && hasL2Link(ticket)
  && !hasL3Link(ticket)
);
const isL3Linked = (ticket) => ticket && hasL3Link(ticket);

const snowCaseLabel = (ticket) => {
  const number = String((ticket && ticket.snowId) || '').trim();
  if (number) return number;
  if ((ticket && ticket.linkType) === 'Service') return 'RITM';
  if ((ticket && ticket.linkType) === 'Incident') return 'Incident';
  return '';
};

const ticketMatchesOverviewChannel = (ticket, key) => {
  const linkType = (ticket && ticket.linkType) || '';
  if (key === 'salesforce') return true;
  if (key === 'l2ado') return isL2OnlyLink(ticket);
  if (key === 'l3ado') return isL3Linked(ticket);
  if (key === 'snow') return isSnowLinkType(linkType);
  return false;
};

// Business rule (confirmed against the actual data): a Salesforce case only
// stops being "open" when it's Closed or Cancelled. Statuses like Delivered,
// Resolved, Completed, or Merged represent an in-progress resolution stage,
// not a closed case -- they must stay counted as open and keep showing their
// real (non-zero) count in the status breakdown. An earlier version of this
// predicate additionally excluded those statuses, which silently zeroed out
// their breakdown rows (they were filtered out of the counts map entirely,
// not counted-and-shown-as-zero) and undercounted the open-case total.
const isSfClosedState = (state) =>
  /^(closed|cancelled|canceled)$/i.test(String(state || '').trim());

const isSfBacklogState = (state) => !isSfClosedState(state);

/** L1 Salesforce "open" KPI: Open + New + Work in Progress only. */
const isSfHotOpenState = (state) => {
  const s = String(state || '').trim().toLowerCase();
  return s === 'open' || s === 'opened' || s === 'new' || s === 'work in progress' || s === 'working' || s === 'wip';
};

/** All Salesforce board: live queue statuses only. */
const SF_ALL_BOARD_STATUSES = ['New', 'Open', 'Work In Progress', 'Fix Applied'];
const isSfAllBoardState = (state) => {
  const s = String(state || '').trim().toLowerCase();
  return s === 'open'
    || s === 'opened'
    || s === 'new'
    || s === 'work in progress'
    || s === 'working'
    || s === 'wip'
    || s === 'fix applied';
};

const CHART_CREATED = '#2b6cb0';
const CHART_CLOSED_BAR = '#666666';
const CHART_RECEIVED_CLOSED = '#2f9e52';

const TicketStatTile = ({
  label,
  value,
  icon: Icon,
  color,
  onClick,
  selected = false,
  aside = null,
  dataSource = null,
  valueLabel = 'Open'
}) => (
  <Paper
    elevation={2}
    component={onClick ? 'button' : 'div'}
    type={onClick ? 'button' : undefined}
    aria-pressed={onClick ? selected : undefined}
    onClick={onClick}
    sx={{
      appearance: 'none',
      boxSizing: 'border-box',
      width: '100%',
      p: 0,
      border: 'none',
      textAlign: 'left',
      font: 'inherit',
      borderRadius: 2,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      outline: selected ? `2px solid ${color}` : 'none',
      outlineOffset: 2,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: onClick ? 'translateY(-2px)' : 'none',
        boxShadow: '0 8px 10px rgba(0,0,0,0.08), 0 3px 6px rgba(0,0,0,0.06)'
      }
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1.5,
        minHeight: 52,
        background: `linear-gradient(135deg, ${color}ee 0%, ${color}bb 100%)`
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Icon sx={{ color: '#fff', fontSize: 18, flexShrink: 0 }} />
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8125rem', lineHeight: 1.2, letterSpacing: 0.3 }}>
          {label}
        </Typography>
      </Box>
      {onClick && <ArrowForwardIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 18, flexShrink: 0 }} />}
    </Box>
    <Box
      sx={{
        bgcolor: '#fff',
        flexGrow: 1,
        px: 1.5,
        py: 1.25,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center'
      }}
    >
      <Typography sx={{ fontWeight: 800, color, fontSize: '2rem', lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, fontWeight: 500 }}>
        {valueLabel}
      </Typography>
      {aside != null && aside !== '' && (
        <Box sx={{ mt: 1 }}>{aside}</Box>
      )}
      {dataSource && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 'auto', pt: 1, fontWeight: 500, fontSize: '0.6rem', lineHeight: 1.2 }}
        >
          Data Source: {dataSource}
        </Typography>
      )}
    </Box>
  </Paper>
);

const UniqueOpenCountsTable = ({
  tickets = [],
  loading,
  onSelectChannel,
  selectedKey,
  openCounts = {},
  sfMonthlySeries = null,
  sfMonthlyLoading = false
}) => {
  const [chartChannel, setChartChannel] = useState('salesforce');

  const activeChannelSeries = useMemo(() => {
    if (chartChannel === 'salesforce' && Array.isArray(sfMonthlySeries) && sfMonthlySeries.length) {
      return sfMonthlySeries;
    }
    const channelTickets = (tickets || []).filter((ticket) => ticketMatchesOverviewChannel(ticket, chartChannel));
    return monthlyFromTickets(
      channelTickets,
      (ticket) => isSfClosedState(ticket.state),
      (ticket) => ticket.resolvedDate
    );
  }, [tickets, chartChannel, sfMonthlySeries]);

  const activeChannelRow = UNIQUE_OPEN_ROWS.find((row) => row.key === chartChannel);
  const channelLabel = activeChannelRow?.label || 'Salesforce';
  // Counts wait on backlog; SF chart waits on lean boardMonthly from /stats.
  const chartLoading = chartChannel === 'salesforce' ? sfMonthlyLoading : loading;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 0.9fr) minmax(0, 1.3fr)' },
        gap: 2,
        mb: 2,
        mt: 2.5,
        alignItems: 'stretch'
      }}
    >
    <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden', position: 'relative', height: '100%' }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: MUTED }}>
          Unique counts
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.75rem', mt: 0.5 }}>
          Salesforce cases by link. Click a row to open that list.
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderCellSx}>
                Channel
              </TableCell>
              <TableCell
                align="right"
                sx={{ ...tableHeaderCellSx, width: '1%', whiteSpace: 'nowrap' }}
              >
                Open
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {UNIQUE_OPEN_ROWS.map((row) => {
              const openValue = loading ? '—' : (openCounts[row.key] ?? 0);
              const selected = selectedKey === row.key;
              return (
                <TableRow
                  key={row.key}
                  hover
                  selected={selected}
                  onClick={() => onSelectChannel && onSelectChannel(row.key)}
                  sx={{
                    bgcolor: row.bg,
                    cursor: onSelectChannel ? 'pointer' : 'default'
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: row.color,
                      fontSize: '0.8125rem',
                      borderLeft: `4px solid ${row.color}`,
                      py: 1.25
                    }}
                  >
                    {row.label}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: row.color, py: 1.25 }}>
                    {openValue}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={chartChannel}
        onChange={(_, next) => { if (next) setChartChannel(next); }}
        sx={{ ...pillGroupSx, flexWrap: 'wrap' }}
        aria-label="Monthly chart channel"
      >
        {UNIQUE_OPEN_ROWS.map((row) => (
          <ToggleButton
            key={row.key}
            value={row.key}
            sx={pillToggleSx({ color: row.color, bgcolor: row.bg })}
          >
            {row.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <BoardOpenedClosedChart
        title={channelLabel}
        subtitle="HighQ"
        series={activeChannelSeries || EMPTY_SERIES}
        loading={chartLoading}
        openedColor={activeChannelRow?.color || L1_ACCENT}
        showReceivedClosed
        sx={{ flex: 1 }}
      />
    </Box>
    </Box>
  );
};

const SF_OVERVIEW_TABS = [
  { id: 'salesforce', label: 'All Salesforce', color: L1_ACCENT },
  { id: 'l2ado', label: 'Linked to L2', color: L2_ACCENT },
  { id: 'l3ado', label: 'Linked to L3', color: L3_ACCENT },
  { id: 'snow', label: 'Linked to ServiceNow', color: INCIDENT_ACCENT }
];

const overviewTabStripSx = {
  display: 'flex',
  gap: { xs: 0.5, sm: 1 },
  flexWrap: 'wrap',
  borderBottom: '1px solid rgba(26,46,34,0.12)'
};

const overviewTabButtonSx = (selected, color) => ({
  appearance: 'none',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  px: { xs: 1.5, sm: 2.5 },
  py: 1.25,
  fontFamily: 'inherit',
  fontWeight: 600,
  fontSize: '0.9375rem',
  letterSpacing: '0.01em',
  color: selected ? color : MUTED,
  borderBottom: selected ? `3px solid ${color}` : '3px solid transparent',
  mb: '-1px',
  '&:hover': { color }
});

const OverviewTabStrip = ({ tabs, value, onChange, getLabel, ariaLabel, sx }) => (
  <Box role="tablist" aria-label={ariaLabel} sx={{ ...overviewTabStripSx, ...sx }}>
    {tabs.map((tab) => {
      const selected = value === tab.id;
      return (
        <Box
          key={tab.id}
          component="button"
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => onChange(tab.id)}
          sx={overviewTabButtonSx(selected, tab.color)}
        >
          {getLabel ? getLabel(tab, selected) : tab.label}
        </Box>
      );
    })}
  </Box>
);

const formatLastSynced = (value) => {
  if (!value) return 'Never';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// absoluteHttpUrl moved to ./assigneeDisplay -- shared with the Top Customers pages.

const countOf = (stats, key) => {
  const row = (stats || []).find((s) => s.key === key);
  return row && row.matched ? row.count : null;
};

const PRIMARY_SF_STATUS_ROWS = [
  { match: /^open$/i, label: 'Open', color: WARN_ORANGE },
  { match: /^new$/i, label: 'New', color: L1_ACCENT },
  { match: /^work\s*in\s*progress$/i, label: 'Work in Progress', color: L2_ACCENT }
];

// A single canonical "is this Salesforce case still open" definition
// (isSfBacklogState, i.e. NOT isSfClosedState -- literal Closed/Cancelled
// only; see the confirmed business rule above isSfClosedState's own
// definition). Every board hero, breakdown, table, and age-card across the
// L1 Salesforce tab goes through this one predicate so they can't
// disagree with each other about the same underlying ticket list -- a
// second, differently-defined duplicate of this used to exist and drift out
// of sync with isSfBacklogState across different widgets.
const countMatchingStatuses = (tickets, isMatch) => {
  const counts = {};
  (tickets || []).forEach((ticket) => {
    const state = String(ticket.state || '').trim();
    if (!state || !isMatch(state)) return;
    counts[state] = (counts[state] || 0) + 1;
  });
  return counts;
};

const sumStatusCounts = (counts) => Object.values(counts || {}).reduce((sum, n) => sum + n, 0);

const countMatchingStatusesFromMap = (statusCounts, isMatch) => {
  const counts = {};
  Object.entries(statusCounts || {}).forEach(([name, n]) => {
    if (!name || !isMatch(name)) return;
    counts[name] = Number(n) || 0;
  });
  return counts;
};

const statusRowsFromCounts = (counts) => PRIMARY_SF_STATUS_ROWS.map(({ match, label, color }) => {
  let value = 0;
  Object.entries(counts || {}).forEach(([name, n]) => {
    if (match.test(name)) value += n;
  });
  return { label, value, color };
});

const PRIORITY_LABELS = { 0: 'Unassigned', 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low' };
const SNOW_PRIORITY_OPTIONS = [
  ['1 - Critical', 'P1 – Critical'],
  ['2 - High', 'P2 – High'],
  ['3 - Moderate', 'P3 – Moderate'],
  ['4 - Low', 'P4 – Low']
];
const SNOW_PRIORITY_LABELS = Object.fromEntries(SNOW_PRIORITY_OPTIONS);
const snowPriorityKey = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return '';
  if (text.includes('critical') || /(^|[^a-z0-9])p1([^a-z0-9]|$)/.test(text)) return '1 - Critical';
  if (text.includes('high') || /(^|[^a-z0-9])p2([^a-z0-9]|$)/.test(text)) return '2 - High';
  if (text.includes('moderate') || text.includes('medium') || /(^|[^a-z0-9])p3([^a-z0-9]|$)/.test(text)) return '3 - Moderate';
  if (text.includes('low') || /(^|[^a-z0-9])p4([^a-z0-9]|$)/.test(text)) return '4 - Low';
  if (/^1(\s|$|-)/.test(text)) return '1 - Critical';
  if (/^2(\s|$|-)/.test(text)) return '2 - High';
  if (/^3(\s|$|-)/.test(text)) return '3 - Moderate';
  if (/^4(\s|$|-)/.test(text)) return '4 - Low';
  return '';
};
const ticketSnowPriority = (ticket) => (ticket && (ticket.snowPriority || ticket.priority)) || '';
const snowPriorityLabel = (value) => SNOW_PRIORITY_LABELS[snowPriorityKey(value)] || String(value || '').trim() || '—';
const SNOW_STATUS_OPTIONS = ['New', 'In Progress', 'Assigned', 'Resolved', 'Closed'];
const SNOW_OPEN_STATUS_OPTIONS = ['New', 'In Progress', 'Assigned'];
const snowStatusCanonical = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return '';
  return SNOW_STATUS_OPTIONS.find((status) => status.toLowerCase() === text) || '';
};
const LAKE_SF_STATUSES = [
  'New',
  'Open',
  'Work In Progress',
  'Fix Applied',
  'In Delivery',
  'Partial Service Restored',
  'Set for Auto Close',
  'Delivered',
  'Partially Completed',
  'Partially Delivered',
  'Completed',
  'Resolved',
  'Closed',
  'Merged',
  'Cancelled'
];
const LAKE_SF_PRIORITIES = [
  '5 - Critical',
  '1 - High',
  '2 - Medium',
  'Medium',
  '3 - Low',
  '4 - Non-Service Affecting'
];
const LAKE_SF_PRIORITY_BUCKET = {
  '5 - Critical': 1,
  '1 - High': 2,
  '2 - Medium': 3,
  Medium: 3,
  '3 - Low': 4,
  '4 - Non-Service Affecting': 4
};
const PRIORITY_STYLES = {
  0: { bgcolor: NEUTRAL_SURFACE, color: MUTED },
  1: { bgcolor: '#d32f2f', color: '#fff' },
  2: { bgcolor: L3_ACCENT, color: '#fff' },
  3: { bgcolor: L2_BADGE_BG, color: L3_ACCENT },
  4: { bgcolor: NEUTRAL_SURFACE, color: MUTED }
};
const SNOW_PRIORITY_STYLES = {
  '1 - Critical': PRIORITY_STYLES[1],
  '2 - High': PRIORITY_STYLES[2],
  '3 - Moderate': PRIORITY_STYLES[3],
  '4 - Low': PRIORITY_STYLES[4]
};
const STATE_STYLES = {
  New: { bgcolor: '#ece9f9', color: L1_ACCENT },
  Open: { bgcolor: WARN_ORANGE_BG, color: WARN_ORANGE },
  'Work In Progress': { bgcolor: '#fff3e0', color: WARN_ORANGE },
  'Fix Applied': { bgcolor: WARN_ORANGE_BG, color: WARN_ORANGE },
  'In Delivery': { bgcolor: '#e3ecf7', color: L2_ACCENT },
  'Partial Service Restored': { bgcolor: '#fff8e1', color: '#b28704' },
  'Set for Auto Close': { bgcolor: WARN_ORANGE_BG, color: WARN_ORANGE },
  Active: { bgcolor: '#e3ecf7', color: L2_ACCENT },
  Triage: { bgcolor: '#fff3e0', color: WARN_ORANGE },
  Backlog: { bgcolor: NEUTRAL_SURFACE, color: INK },
  Blocked: { bgcolor: '#fdecea', color: '#c62828' },
  'Response Awaited': { bgcolor: '#fff8e1', color: '#b28704' },
  'Waiting Response': { bgcolor: '#fff8e1', color: '#b28704' },
  Resolved: { bgcolor: '#e4f5e9', color: '#2f9e52' },
  Done: { bgcolor: '#e4f5e9', color: '#2f9e52' },
  Closed: { bgcolor: NEUTRAL_SURFACE, color: MUTED },
  Merged: { bgcolor: NEUTRAL_SURFACE, color: MUTED },
  Cancelled: { bgcolor: NEUTRAL_SURFACE, color: MUTED },
  Delivered: { bgcolor: '#e4f5e9', color: '#2f9e52' },
  Completed: { bgcolor: '#e4f5e9', color: '#2f9e52' },
  'Partially Delivered': { bgcolor: '#fff8e1', color: '#b28704' },
  'Partially Completed': { bgcolor: '#fff8e1', color: '#b28704' },
  'Pending Validation': { bgcolor: '#fff3e0', color: WARN_ORANGE },
  'In Progress': { bgcolor: '#fff3e0', color: WARN_ORANGE },
  Assigned: { bgcolor: '#e3ecf7', color: L2_ACCENT }
};

const statusChip = (value) => (
  <Chip
    size="small"
    label={value || '—'}
    sx={{
      height: 22,
      fontSize: '0.75rem',
      fontWeight: 600,
      ...(value
        ? (STATE_STYLES[value] || { bgcolor: NEUTRAL_SURFACE, color: INK })
        : { bgcolor: NEUTRAL_SURFACE, color: MUTED })
    }}
  />
);

const PRIMARY_ADO_STATUS_ROWS = [
  { match: /^new$/i, label: 'New', color: L1_ACCENT },
  { match: /^active$/i, label: 'Active', color: L2_ACCENT },
  { match: /^triage$/i, label: 'Triage', color: WARN_ORANGE },
  { match: /^backlog$/i, label: 'Backlog', color: INK },
  { match: /^blocked$/i, label: 'Blocked', color: '#c62828' },
  { match: /^response awaited$/i, label: 'Response Awaited', color: '#b28704' },
  { match: /^waiting response$/i, label: 'Waiting Response', color: '#b28704' },
  { match: /^pending validation$/i, label: 'Pending Validation', color: WARN_ORANGE },
  { match: /^resolved$/i, label: 'Resolved', color: '#2f9e52' },
  { match: /^done$/i, label: 'Done', color: '#2f9e52' },
  { match: /^closed$/i, label: 'Closed', color: MUTED }
];

const countTicketStatuses = (tickets) => {
  const counts = {};
  (tickets || []).forEach((ticket) => {
    const state = String(ticket.state || '').trim();
    if (!state) return;
    counts[state] = (counts[state] || 0) + 1;
  });
  return counts;
};

const snowStatusRowsFromCounts = (counts) => (
  SNOW_OPEN_STATUS_OPTIONS.map((label) => {
    let value = 0;
    Object.entries(counts || {}).forEach(([name, n]) => {
      if (snowStatusCanonical(name) === label) value += n;
    });
    return {
      label,
      value,
      color: (STATE_STYLES[label] || {}).color || MUTED
    };
  })
);

const adoStatusRowsFromCounts = (counts) => {
  const used = new Set();
  const rows = PRIMARY_ADO_STATUS_ROWS.map(({ match, label, color }) => {
    let value = 0;
    Object.entries(counts || {}).forEach(([name, n]) => {
      if (!match.test(name)) return;
      used.add(name);
      value += n;
    });
    return { label, value, color };
  }).filter((row) => row.value > 0);
  Object.entries(counts || {})
    .filter(([name, value]) => value > 0 && !used.has(name))
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .forEach(([name, value]) => {
      rows.push({
        label: name,
        value,
        color: (STATE_STYLES[name] || {}).color || MUTED
      });
    });
  return rows;
};

const countFieldStatuses = (tickets, field) => {
  const counts = {};
  (tickets || []).forEach((ticket) => {
    const state = String((ticket && (ticket[field] || ticket.adoState)) || '').trim();
    if (!state) return;
    counts[state] = (counts[state] || 0) + 1;
  });
  return counts;
};

const isAdoDoneOrClosedStatus = (state) =>
  /\b(closed|done|completed|resolved|removed|cancelled|canceled)\b/i.test(String(state || '').trim());

const isSnowClosedState = (state) =>
  /\b(closed|resolved|cancelled|canceled)\b/i.test(String(state || '').trim());

const snowIncidentUrl = (instanceUrl, sysId) => {
  const base = String(instanceUrl || '').replace(/\/+$/, '');
  if (!base || !sysId) return '';
  return `${base}/nav_to.do?uri=incident.do?sys_id=${encodeURIComponent(sysId)}`;
};

const mapPortalSnowTickets = (tickets, instanceUrl) =>
  (tickets || []).map((ticket) => ({
    id: ticket.number || ticket.sysId || '',
    title: ticket.shortDescription || '',
    state: ticket.state || '',
    createdDate: ticket.openedAt || null,
    resolvedDate: ticket.resolvedAt || ticket.closedAt || null,
    priority: ticket.priority ?? null,
    assignedTo: ticket.assignedTo || '',
    service: ticket.service || ticket.serviceTag || '',
    url: snowIncidentUrl(instanceUrl, ticket.sysId),
    linkType: 'Incident'
  }));

const linkedAdoStatus = (ticket, statusField) =>
  String((ticket && (ticket[statusField] || ticket.adoState)) || '').trim();

/** Open Salesforce case with a linked ADO ticket in any state, including Done/Closed. */
const isLinkedAdoRow = (ticket, statusField) =>
  Boolean(ticket) && isSfBacklogState(ticket.state) && Boolean(linkedAdoStatus(ticket, statusField));

/** Same, but the ADO ticket itself is still open (counts on the L2/L3 cards). */
const isLinkedAdoOpenRow = (ticket, statusField) =>
  isLinkedAdoRow(ticket, statusField) && !isAdoDoneOrClosedStatus(linkedAdoStatus(ticket, statusField));

const linkedAdoBoardFromCases = (tickets, statusField, missingLabel) => {
  const linked = (tickets || []).filter((ticket) => isLinkedAdoOpenRow(ticket, statusField));
  const counts = countFieldStatuses(linked, statusField);
  const rows = adoStatusRowsFromCounts(counts);
  const withStatus = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const missing = linked.length - withStatus;
  if (missing > 0) {
    rows.push({ label: missingLabel, value: missing, color: MUTED });
  }
  return { hero: linked.length, rows };
};

/** Overview ServiceNow card: L1 Salesforce cases linked to ServiceNow (same idea as L2/L3). */
const linkedSnowBoardFromCases = (tickets) => {
  const linked = tickets || [];
  const counts = {};
  linked.forEach((ticket) => {
    const raw = String((ticket && ticket.snowState) || '').trim();
    const state = snowStatusCanonical(raw) || raw || 'No ServiceNow status';
    counts[state] = (counts[state] || 0) + 1;
  });
  const used = new Set();
  const rows = SNOW_STATUS_OPTIONS.map((label) => {
    const value = Number(counts[label]) || 0;
    if (value > 0) used.add(label);
    return {
      label,
      value,
      color: (STATE_STYLES[label] || {}).color || MUTED
    };
  }).filter((row) => row.value > 0);
  Object.entries(counts)
    .filter(([name, value]) => value > 0 && !used.has(name))
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .forEach(([name, value]) => {
      rows.push({
        label: name,
        value,
        color: (STATE_STYLES[name] || {}).color || MUTED
      });
    });
  return { hero: linked.length, rows };
};

const TYPE_LABELS = { Bug: 'Bug', 'Product Backlog Item': 'PBI' };
const LOCATION_STYLES = {
  L1: { bgcolor: L1_BADGE_BG, color: L1_ACCENT },
  L2: { bgcolor: L2_BADGE_BG, color: L2_ACCENT },
  L3: { bgcolor: L3_BADGE_BG, color: L3_ACCENT }
};
const LINK_TYPE_STYLES = {
  ADO: { bgcolor: L2_BADGE_BG, color: L2_ACCENT },
  Incident: { bgcolor: INCIDENT_BADGE_BG, color: INCIDENT_ACCENT },
  Service: { bgcolor: INCIDENT_BADGE_BG, color: INCIDENT_ACCENT },
  ServiceNow: { bgcolor: INCIDENT_BADGE_BG, color: INCIDENT_ACCENT }
};

const pillToggleSx = (style) => ({
  color: style.color,
  borderColor: `${style.color}40 !important`,
  bgcolor: '#fff',
  '&.Mui-selected': {
    bgcolor: style.bgcolor,
    color: style.color,
    borderColor: `${style.color}80 !important`,
    '&:hover': { bgcolor: style.bgcolor }
  },
  '&:hover': { bgcolor: style.bgcolor }
});

const pillGroupSx = {
  gap: 0.5,
  '& .MuiToggleButtonGroup-grouped': {
    border: '1px solid rgba(0,0,0,0.08) !important',
    borderRadius: '8px !important',
    mx: 0,
    px: 1.25,
    py: 0.25,
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'none'
  }
};

const AGE_BUCKETS = [
  { key: 'lt7', label: '< 7 days', color: '#2f9e52', track: '#e4f5e9' },
  { key: 'd7_30', label: '7–30 days', color: '#2b6cb0', track: '#e3ecf7' },
  { key: 'd30_90', label: '30–90 days', color: WARN_ORANGE, track: WARN_ORANGE_BG },
  { key: 'd90', label: '90+ days', color: '#D74B00', track: '#fde8dc' }
];

const MONTH_OPTIONS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
];

// Self-extending like BOARD_CHART_YEARS below, so this doesn't silently stop
// including the current year once 2026 has passed.
const DEFAULT_FILTER_YEARS = [new Date().getFullYear(), new Date().getFullYear() - 1];

const ticketOpenedAt = (ticket) => {
  if (!ticket || !ticket.createdDate) return null;
  const opened = new Date(ticket.createdDate);
  return Number.isNaN(opened.getTime()) ? null : opened;
};

const formatOpenedDay = (ticket) => {
  const opened = ticketOpenedAt(ticket);
  if (!opened) return '—';
  return opened.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// UTC, not local time -- matches monthKeyOf below so a ticket near a month
// boundary lands in the same year/month here as it does in the chart, and
// this filter's dropdown selection actually matches what the chart shows.
const matchesYearMonth = (ticket, yearFilter, monthFilter) => {
  const yearsAll = !yearFilter?.length || isAllFilter(yearFilter);
  const monthsAll = !monthFilter?.length || isAllFilter(monthFilter);
  if (yearsAll && monthsAll) return true;
  const opened = ticketOpenedAt(ticket);
  if (!opened) return false;
  if (!yearsAll && !yearFilter.map(String).includes(String(opened.getUTCFullYear()))) return false;
  if (!monthsAll && !monthFilter.map(String).includes(String(opened.getUTCMonth() + 1))) return false;
  return true;
};

// Grows forward from when tracking started so charts don't silently drop
// data once the calendar passes the last hardcoded year.
const CHART_TRACKING_START_YEAR = 2025;
const CHART_CURRENT_YEAR = new Date().getFullYear();
const BOARD_CHART_YEARS = Array.from(
  { length: Math.max(CHART_CURRENT_YEAR, CHART_TRACKING_START_YEAR) - CHART_TRACKING_START_YEAR + 1 },
  (_, i) => CHART_TRACKING_START_YEAR + i
);
// Stable empty-array reference -- a fresh `[]` literal as a prop defeats the
// consuming component's useMemo([series, ...]) every render.
const EMPTY_SERIES = [];
const BOARD_MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const monthlyFromTickets = (tickets, isClosed, closedDateOf) => {
  const map = {};
  BOARD_CHART_YEARS.forEach((year) => {
    for (let month = 1; month <= 12; month += 1) {
      const key = `${year}-${String(month).padStart(2, '0')}`;
      map[key] = {
        key,
        year,
        month,
        label: `${BOARD_MONTH_FULL[month - 1]} ${year}`,
        opened: 0,
        closed: 0,
        receivedClosed: 0
      };
    }
  });
  // UTC, not local time -- matches the server's toMonthKey (monthlyOpenedClosed.js)
  // so a case near a month boundary buckets the same way regardless of the
  // viewer's browser timezone or the server's timezone.
  const monthKeyOf = (date) => {
    if (!date) return null;
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return null;
    const year = parsed.getUTCFullYear();
    if (!BOARD_CHART_YEARS.includes(year)) return null;
    return `${year}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}`;
  };
  (tickets || []).forEach((ticket) => {
    const openedKey = monthKeyOf(ticket.createdDate);
    if (openedKey && map[openedKey]) map[openedKey].opened += 1;
    if (isClosed(ticket)) {
      const closedKey = monthKeyOf(closedDateOf(ticket));
      if (closedKey && map[closedKey]) {
        map[closedKey].closed += 1;
        if (openedKey && openedKey === closedKey) map[closedKey].receivedClosed += 1;
      }
    }
  });
  return Object.values(map).sort((a, b) => a.key.localeCompare(b.key));
};

const BoardOpenedClosedChart = ({
  title,
  subtitle,
  series = [],
  loading = false,
  openedColor = L1_ACCENT,
  showReceivedClosed = false,
  sx = {}
}) => {
  const [chartYear, setChartYear] = useState('all');
  const yearLabel = chartYear === 'all' ? 'All' : chartYear;
  const openedFill = showReceivedClosed ? CHART_CREATED : openedColor;
  const closedFill = CHART_CLOSED_BAR;
  const receivedFill = CHART_RECEIVED_CLOSED;
  const createdLabel = showReceivedClosed ? 'Created' : 'Opened';
  const colorKeys = [
    { color: openedFill, label: createdLabel, meaning: showReceivedClosed ? 'this month only — not previous months' : 'created that month' },
    { color: closedFill, label: 'Closed', meaning: 'closed that month, including older items' },
    ...(showReceivedClosed
      ? [{ color: receivedFill, label: 'Received & closed', meaning: 'created and closed in the same month' }]
      : [])
  ];
  const chartRows = useMemo(() => {
    // UTC, matching the row keys themselves (monthKeyOf/toMonthKey) -- a
    // local-time comparison here could hide or show the current month a few
    // hours early/late relative to what's actually in `series`.
    const now = new Date();
    const currentKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
    return (series || []).filter((row) => {
      if (chartYear !== 'all' && row.year !== Number(chartYear)) return false;
      if (chartYear === 'all' && row.key > currentKey) return false;
      return true;
    }).map((row) => ({
      ...row,
      opened: row.opened || 0,
      closed: row.closed || 0,
      receivedClosed: row.receivedClosed || 0,
      tick: chartYear === 'all'
        ? `${BOARD_MONTH_FULL[row.month - 1] || ''} ${row.year}`
        : (BOARD_MONTH_FULL[row.month - 1] || '')
    }));
  }, [series, chartYear]);

  return (
    <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden', p: '16px 20px', minHeight: 300, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: MUTED }}>
            {showReceivedClosed ? 'Monthly · created, closed, received & closed' : 'Monthly · opened vs closed'}
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: '0.75rem', mt: 0.5 }}>
            {title} · {yearLabel}{subtitle ? ` · ${subtitle}` : ''}
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          label="Year"
          value={chartYear}
          onChange={(e) => setChartYear(e.target.value)}
          SelectProps={{ native: true }}
          sx={{ minWidth: 110, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }}
        >
          <option value="all">All</option>
          {BOARD_CHART_YEARS.map((year) => (
            <option key={year} value={String(year)}>{year}</option>
          ))}
        </TextField>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
        {colorKeys.map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: item.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.75rem', color: INK, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {item.label}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>
              {item.meaning}
            </Typography>
          </Box>
        ))}
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{ height: 240, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 56 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(26,46,34,0.08)" />
              <XAxis
                dataKey="tick"
                interval={chartYear === 'all' ? 1 : 0}
                tick={{ fontSize: 11, fill: MUTED }}
                angle={-40}
                textAnchor="end"
                height={64}
              />
              <YAxis tick={{ fontSize: 11, fill: MUTED }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)', fontSize: 12 }} />
              <Bar dataKey="opened" name={createdLabel} fill={openedFill} radius={[4, 4, 0, 0]} />
              <Bar dataKey="closed" name="Closed" fill={closedFill} radius={[4, 4, 0, 0]} />
              {showReceivedClosed && (
                <Bar dataKey="receivedClosed" name="Received & closed" fill={receivedFill} radius={[4, 4, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

const daysOpen = (date) => {
  if (!date) return null;
  const ms = Date.now() - new Date(date).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  return ms / 86400000;
};

const SF_COL_WIDTH = {
  'Case L1': { width: '5.62rem', maxWidth: '6.88rem' },
  'Case L2': { width: '5.62rem', maxWidth: '6.88rem' },
  'Case L3': { width: '5.62rem', maxWidth: '6.88rem' },
  'Case ServiceNow': { width: '6.88rem', maxWidth: '8.12rem' },
  Subject: { minWidth: '12.5rem', maxWidth: '22.5rem' },
  Age: { width: '4.06rem', maxWidth: '4.69rem' },
  'Created Date': { width: '9.38rem', maxWidth: '10.62rem' },
  'Last Modified Date': { width: '9.38rem', maxWidth: '10.62rem' },
  'Salesforce Status': { width: '7.5rem', maxWidth: '9.38rem' },
  'L2 Status': { width: '6.88rem', maxWidth: '8.75rem' },
  'L3 Status': { width: '6.88rem', maxWidth: '8.75rem' },
  'ServiceNow Status': { width: '7.5rem', maxWidth: '9.38rem' },
  'SNOW Status': { width: '7.5rem', maxWidth: '9.38rem' },
  'L1 Assignee': { width: '8.75rem', maxWidth: '10.62rem' },
  'L2 Assignee': { width: '6.88rem', maxWidth: '8.44rem' },
  'L3 Assignee': { width: '6.88rem', maxWidth: '8.44rem' },
  'ServiceNow Assignee': { width: '8.75rem', maxWidth: '10.62rem' },
  Version: { width: '5.62rem', maxWidth: '6.88rem' },
  Comments: { width: '3.75rem', maxWidth: '4.38rem' }
};

const SF_HEADER_LABEL = {
  'Created Date': 'Created',
  'Last Modified Date': 'Modified',
  'Salesforce Status': 'L1 Status',
  'Case L2': 'L2 Ticket',
  'Case L3': 'L3 Ticket',
  'Case ServiceNow': 'ServiceNow'
};

const sfHeaderCellSx = (col, isFullSfView) => ({
  ...tableHeaderCellSx,
  fontSize: '0.875rem',
  letterSpacing: '0.01em',
  whiteSpace: 'nowrap',
  ...(SF_COL_WIDTH[col] || { width: '1%' }),
  ...(isFullSfView && (col === 'L2 Assignee' || col === 'L3 Assignee')
    ? { maxWidth: '8.4rem', width: '8.4rem' }
    : null)
});

// Same table style as the L2 Open list: theme cell padding and 0.875rem text.
const sfBodyCellSx = (col) => ({
  fontSize: '0.875rem',
  color: INK,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...(SF_COL_WIDTH[col] || { width: '1%' })
});

const ageBucketKey = (date) => {
  const days = daysOpen(date);
  if (days == null) return null;
  if (days < 7) return 'lt7';
  if (days < 30) return 'd7_30';
  if (days < 90) return 'd30_90';
  return 'd90';
};

// displayAssigneeName / ownerFromAssignedTo / isUnassigned / AssigneeCell / CaseIdLink /
// absoluteHttpUrl moved to ./assigneeDisplay -- shared with the Top Customers pages.
// escapeRegExp/mentionToken moved to ./useAdoCommentComposer -- only the comment composer used them.

const EMPTY_L2_STATUS = 'No L2 status';
const EMPTY_L3_STATUS = 'No L3 status';
const EMPTY_SNOW_STATUS = 'No ServiceNow status';

const adoLinkedStatusOf = (ticket, field, emptyLabel) => {
  const value = String((ticket && (ticket[field] || ticket.adoState)) || '').trim();
  return value || emptyLabel;
};

const SF_NUMERIC_COLUMNS = new Set([
  'Case L1', 'Case L2', 'Case L3', 'Age', 'Created Date', 'Last Modified Date'
]);

const sfSortValue = (ticket, col) => {
  switch (col) {
    case 'Case L1':
      return Number(ticket.id) || NaN;
    case 'Case L2': {
      const shown = pickTicketId(l2IdsOf(ticket), linkedAdoIdOf(ticket));
      return Number(shown) || shown || '';
    }
    case 'Case L3': {
      const shown = pickTicketId(ticket.l3Ids || [], linkedAdoIdOf(ticket));
      return Number(shown) || shown || '';
    }
    case 'Case ServiceNow':
      return snowCaseLabel(ticket) || ticket.snowId || ticket.externalId || '';
    case 'Subject':
      return ticket.title || '';
    case 'Age':
    case 'Created Date': {
      const t = ticket.createdDate ? new Date(ticket.createdDate).getTime() : NaN;
      return t;
    }
    case 'Last Modified Date': {
      const t = ticket.lastModifiedDate ? new Date(ticket.lastModifiedDate).getTime() : NaN;
      return t;
    }
    case 'Salesforce Status':
      return ticket.state || '';
    case 'SNOW Status':
      return ticket.snowState || '';
    // The status of the L2 / L3 ticket shown; blank sorts last. (adoLinkedStatusOf falls back to the case's main
    // ticket state, which would sort a case with no L3 ticket among the L3 statuses.)
    case 'L2 Status':
      return String(ticket.l2State || '').trim();
    case 'L3 Status':
      return String(ticket.l3State || '').trim();
    case 'ServiceNow Status':
      return ticket.snowState || EMPTY_SNOW_STATUS;
    case 'L1 Assignee':
      return displayAssigneeName(ticket.assignedTo);
    case 'L2 Assignee':
      return displayAssigneeName(ticket.l2AssignedTo);
    case 'L3 Assignee':
      return displayAssigneeName(ticket.l3AssignedTo);
    case 'ServiceNow Assignee':
      return displayAssigneeName(ticket.snowAssignedTo);
    case 'Version':
      return ticket.adoFixVersion || ticket.fixVersion || '';
    case 'Comments':
      return ticket.l2Id || (ticket.l3Ids && ticket.l3Ids[0]) || '';
    default:
      return '';
  }
};

const isSortValueEmpty = (value) =>
  value == null
  || value === ''
  || (typeof value === 'number' && Number.isNaN(value));

const compareSfTickets = (a, b, sortBy, sortDir) => {
  const aVal = sfSortValue(a, sortBy);
  const bVal = sfSortValue(b, sortBy);
  const aEmpty = isSortValueEmpty(aVal);
  const bEmpty = isSortValueEmpty(bVal);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let cmp;
  if (SF_NUMERIC_COLUMNS.has(sortBy) && typeof aVal === 'number' && typeof bVal === 'number') {
    cmp = aVal - bVal;
  } else {
    cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base', numeric: true });
  }
  // Age asc = low age first (newer cases) — invert timestamp order.
  if (sortBy === 'Age') cmp = -cmp;
  return sortDir === 'asc' ? cmp : -cmp;
};

const TicketBacklogTable = React.memo(({
  tickets = [],
  title = 'Investigation cases',
  accent = L2_ACCENT,
  ageBucket = 'all',
  onClearAge,
  badgeBg = L2_BADGE_BG,
  emptyMessage = 'No open Investigation backlog items.',
  showType = false,
  showProductFilter = false,
  showChannelFilter = true,
  sfColumnSet = 'full',
  openBacklogOnly = false,
  initialStatusFilter,
  initialRowsPerPage = 100,
  locationFilter: locationFilterProp,
  onLocationFilterChange,
  linkTypeFilter: linkTypeFilterProp,
  onLinkTypeFilterChange,
  priorityKind = 'auto'
}) => {
  const [statusFilter, setStatusFilter] = useState(
    Array.isArray(initialStatusFilter) ? initialStatusFilter : [initialStatusFilter || ALL_FILTER]
  );
  const [priorityFilter, setPriorityFilter] = useState([ALL_FILTER]);
  const [linkTypeFilterState, setLinkTypeFilterState] = useState('all');
  const [locationFilterState, setLocationFilterState] = useState([]);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [linkedOnly, setLinkedOnly] = useState(false);
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [journeyTicket, setJourneyTicket] = useState(null);
  const [caseDetailsId, setCaseDetailsId] = useState(null);
  const [adoDetails, setAdoDetails] = useState(null);
  const [snowDetails, setSnowDetails] = useState(null);
  const [caseIdSearch, setCaseIdSearch] = useState('');
  const [yearFilter, setYearFilter] = useState([ALL_FILTER]);
  const [monthFilter, setMonthFilter] = useState([ALL_FILTER]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortBy, setSortBy] = useState('Age');
  const [sortDir, setSortDir] = useState('asc');
  const {
    commentTicket,
    comments,
    commentsLoading,
    commentsError,
    commentDraft,
    setCommentDraft,
    commentMentions,
    commentPosting,
    commentPostError,
    identityOptions,
    identityLoading,
    identityError,
    identityInput,
    setIdentityInput,
    openComments,
    closeComments,
    searchIdentities,
    handleMentionsChange,
    handlePostComment,
    canPostComment
  } = useAdoCommentComposer();
  const isL2SfView = showProductFilter && sfColumnSet === 'l2';
  const isL3SfView = showProductFilter && sfColumnSet === 'l3';
  const isSnowSfView = showProductFilter && sfColumnSet === 'snow';
  const isFullSfView = showProductFilter && sfColumnSet === 'full';
  const useSnowPriority = priorityKind === 'servicenow' || (priorityKind === 'auto' && isSnowSfView);
  const isSnowTable = useSnowPriority && !showProductFilter;
  const locationFilter = locationFilterProp !== undefined ? locationFilterProp : locationFilterState;
  const setLocationFilter = onLocationFilterChange || setLocationFilterState;
  const linkTypeFilter = linkTypeFilterProp !== undefined ? linkTypeFilterProp : linkTypeFilterState;
  const setLinkTypeFilter = onLinkTypeFilterChange || setLinkTypeFilterState;
  const ageLabel = AGE_BUCKETS.find((b) => b.key === ageBucket)?.label;

  const yearOptions = useMemo(() => {
    const years = new Set(DEFAULT_FILTER_YEARS);
    (tickets || []).forEach((ticket) => {
      const opened = ticketOpenedAt(ticket);
      if (opened) years.add(opened.getUTCFullYear());
    });
    return [...years].sort((a, b) => b - a);
  }, [tickets]);

  const statusOptions = useMemo(() => {
    if (isL2SfView || isL3SfView) {
      const emptyLabel = isL2SfView ? EMPTY_L2_STATUS : EMPTY_L3_STATUS;
      const field = isL2SfView ? 'l2State' : 'l3State';
      const fromTickets = [...new Set((tickets || []).map((ticket) => adoLinkedStatusOf(ticket, field, emptyLabel)))];
      return fromTickets.sort((a, b) => {
        if (a === emptyLabel) return 1;
        if (b === emptyLabel) return -1;
        return a.localeCompare(b);
      });
    }
    if (useSnowPriority) {
      return SNOW_STATUS_OPTIONS;
    }
    if (isFullSfView) {
      return SF_ALL_BOARD_STATUSES;
    }
    const fromTickets = [...new Set((tickets || []).map((t) => t.state).filter(Boolean))];
    if (showProductFilter) {
      const known = new Set(LAKE_SF_STATUSES);
      fromTickets.forEach((status) => known.add(status));
      const extra = [...known].filter((status) => !LAKE_SF_STATUSES.includes(status)).sort((a, b) => a.localeCompare(b));
      return [...LAKE_SF_STATUSES, ...extra];
    }
    return fromTickets.length > 0 ? fromTickets.sort((a, b) => a.localeCompare(b)) : ['New', 'Active', 'Triage', 'Backlog', 'Blocked', 'Response Awaited'];
  }, [tickets, showProductFilter, isL2SfView, isL3SfView, isFullSfView, useSnowPriority]);

  const priorityOptions = useMemo(() => {
    if (useSnowPriority) {
      const known = new Set(SNOW_PRIORITY_OPTIONS.map(([value]) => value));
      const extra = [...new Set((tickets || []).map((ticket) => snowPriorityKey(ticketSnowPriority(ticket)) || String(ticketSnowPriority(ticket) || '').trim()).filter(Boolean))]
        .filter((value) => !known.has(value))
        .sort((a, b) => a.localeCompare(b));
      return [...SNOW_PRIORITY_OPTIONS, ...extra.map((value) => [value, value])];
    }
    if (!showProductFilter) {
      return Object.entries(PRIORITY_LABELS);
    }
    const fromTickets = [...new Set((tickets || []).map((t) => t.lakePriority).filter(Boolean))];
    const known = new Set(LAKE_SF_PRIORITIES);
    fromTickets.forEach((label) => known.add(label));
    const extra = [...known].filter((label) => !LAKE_SF_PRIORITIES.includes(label)).sort((a, b) => a.localeCompare(b));
    return [...LAKE_SF_PRIORITIES, ...extra].map((label) => [label, label]);
  }, [tickets, showProductFilter, useSnowPriority]);

  const ticketLocation = (ticket) => ticket.location || 'L1';
  const ticketLinkType = (ticket) => ticket.linkType || 'None';

  const dateScopedTickets = useMemo(() => {
    if (!showProductFilter) return tickets || [];
    return (tickets || []).filter((ticket) => matchesYearMonth(ticket, yearFilter, monthFilter));
  }, [tickets, showProductFilter, yearFilter, monthFilter]);

  const filtered = useMemo(() => {
    const caseQuery = caseIdSearch.trim().toLowerCase();
    return dateScopedTickets.filter((ticket) => {
      if (caseQuery) {
        const haystack = [
          ticket.id,
          isSnowTable ? ticket.title : '',
          isSnowTable ? ticket.service : ''
        ].join(' ').toLowerCase();
        if (!haystack.includes(caseQuery)) return false;
      }
      if (isL2SfView) {
        if (!filterAllows(statusFilter, adoLinkedStatusOf(ticket, 'l2State', EMPTY_L2_STATUS))) return false;
      } else if (isL3SfView) {
        if (!filterAllows(statusFilter, adoLinkedStatusOf(ticket, 'l3State', EMPTY_L3_STATUS))) return false;
      } else if (useSnowPriority) {
        const raw = isSnowSfView ? ticket.snowState : ticket.state;
        if (!filterAllows(statusFilter, snowStatusCanonical(raw) || String(raw || '').trim() || EMPTY_SNOW_STATUS)) return false;
      } else if (!filterAllows(statusFilter, ticket.state)) {
        return false;
      }
      if (isAllFilter(priorityFilter) || !priorityFilter.length) {
        // no priority restriction
      } else {
        const matchesPriority = priorityFilter.some((selected) => {
          if (useSnowPriority) {
            const key = snowPriorityKey(ticketSnowPriority(ticket));
            return key ? key === selected : String(ticketSnowPriority(ticket)) === String(selected);
          }
          if (showProductFilter) {
            if (ticket.lakePriority) return ticket.lakePriority === selected;
            const bucket = LAKE_SF_PRIORITY_BUCKET[selected];
            return bucket != null && Number(ticket.priority) === bucket;
          }
          return String(ticket.priority) === String(selected);
        });
        if (!matchesPriority) return false;
      }
      if (showProductFilter && showChannelFilter && linkTypeFilter !== 'all') {
        if (linkTypeFilter === 'ServiceNow') {
          if (!isSnowLinkType(ticket.linkType)) return false;
        } else if (ticketLinkType(ticket) !== linkTypeFilter) return false;
      }
      if (showProductFilter && showChannelFilter && locationFilter.length > 0 && !locationFilter.includes(ticketLocation(ticket))) return false;
      if (unassignedOnly && !isUnassigned(ticket.assignedTo)) return false;
      if (linkedOnly && !(hasL2Link(ticket) || hasL3Link(ticket) || isSnowLinkType(ticket.linkType))) return false;
      if (blockedOnly && ![ticket.state, ticket.l2State, ticket.l3State, ticket.snowState].some(isBlockedStatus)) return false;
      if (ageBucket !== 'all' && ageBucketKey(ticket.createdDate) !== ageBucket) return false;
      return true;
    });
  }, [dateScopedTickets, caseIdSearch, statusFilter, priorityFilter, linkTypeFilter, locationFilter, unassignedOnly, linkedOnly, blockedOnly, ageBucket, showProductFilter, showChannelFilter, isL2SfView, isL3SfView, isSnowSfView, useSnowPriority, isSnowTable]);

  useEffect(() => {
    setPage(0);
  }, [caseIdSearch, statusFilter, priorityFilter, linkTypeFilter, locationFilter, unassignedOnly, linkedOnly, blockedOnly, ageBucket, sortBy, sortDir, yearFilter, monthFilter, tickets]);

  const sortedFiltered = useMemo(() => {
    if (!showProductFilter || !sortBy) return filtered;
    const list = [...filtered];
    list.sort((a, b) => compareSfTickets(a, b, sortBy, sortDir));
    return list;
  }, [filtered, showProductFilter, sortBy, sortDir]);

  const handleSfSort = (col) => {
    if (sortBy === col) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const visible = rowsPerPage > 0
    ? sortedFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedFiltered;

  const sfDateHeaders = ['Created Date', 'Last Modified Date'];
  const sfTableHeaders = isL2SfView
    ? ['Case L1', 'Case L2', 'Subject', 'Age', ...sfDateHeaders, 'Salesforce Status', 'L2 Status', 'L1 Assignee', 'L2 Assignee', 'Comments']
    : isL3SfView
      ? ['Case L1', 'Case L2', 'Case L3', 'Subject', 'Age', ...sfDateHeaders, 'Salesforce Status', 'L3 Status', 'L1 Assignee', 'L2 Assignee', 'L3 Assignee', 'Version', 'Comments']
      : isSnowSfView
        ? ['Case L1', 'Case ServiceNow', 'Subject', 'Age', ...sfDateHeaders, 'Salesforce Status', 'ServiceNow Status', 'L1 Assignee', 'ServiceNow Assignee']
        : ['Case L1', 'Case L2', 'Case L3', 'Case ServiceNow', 'Subject', 'Age', ...sfDateHeaders, 'Salesforce Status', 'L2 Status', 'L3 Status', 'SNOW Status', 'Version'];
  const snowTableHeaders = ['Incident #', 'Description', 'State', 'Priority', 'Service', 'Assigned To', 'Opened'];
  const adoTableHeaders = ['Case #', ...(showType ? ['Type'] : []), 'Subject', 'Status', 'Version', 'Priority', 'Owner', 'Comments'];
  const tableHeaders = showProductFilter ? sfTableHeaders : (isSnowTable ? snowTableHeaders : adoTableHeaders);
  const totalNoun = isSnowTable
    ? (tickets.length === 1 ? 'incident' : 'incidents')
    : (tickets.length === 1 ? 'case' : 'cases');

  const filterFieldSx = {
    minWidth: 140,
    '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' }
  };

  return (
  <Paper
    elevation={0}
    sx={{
      borderRadius: '12px',
      border: '1px solid rgba(0,0,0,0.05)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
      overflow: 'visible',
      width: '100%'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, px: 3, pt: 2, pb: 1.5 }}>
      <Typography sx={{ fontWeight: 600, color: accent, fontSize: showProductFilter ? '1.25rem' : '1rem' }}>
        {title}
      </Typography>
      <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>
        {filtered.length === tickets.length
          ? `${tickets.length} ${totalNoun}`
          : `${filtered.length} of ${tickets.length} ${totalNoun}`}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: 3, pb: 2 }}>
      <TextField
        size="small"
        label={isSnowTable ? 'Incident #' : 'Case #'}
        placeholder={isSnowTable ? 'Search INC number' : 'Search case ID'}
        value={caseIdSearch}
        onChange={(e) => setCaseIdSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
            </InputAdornment>
          )
        }}
        sx={{ ...filterFieldSx, minWidth: 180 }}
      />
      <MultiCheckSelect
        label={isL2SfView ? 'L2 Status' : isL3SfView ? 'L3 Status' : isSnowSfView ? 'ServiceNow Status' : 'Status'}
        value={statusFilter}
        onChange={setStatusFilter}
        options={statusOptions.map((status) => ({ value: status, label: status }))}
        sx={filterFieldSx}
        minWidth={isL2SfView || isL3SfView || isSnowSfView ? 180 : 140}
      />
      <MultiCheckSelect
        label="Priority"
        value={priorityFilter}
        onChange={setPriorityFilter}
        options={priorityOptions.map(([value, label]) => ({ value, label }))}
        sx={filterFieldSx}
        minWidth={showProductFilter || useSnowPriority ? 220 : 140}
      />
      {showProductFilter && (
        <MultiCheckSelect
          label="Year"
          value={yearFilter}
          onChange={setYearFilter}
          options={yearOptions.map((year) => ({ value: String(year), label: String(year) }))}
          sx={filterFieldSx}
          minWidth={110}
        />
      )}
      {showProductFilter && (
        <MultiCheckSelect
          label="Month"
          value={monthFilter}
          onChange={setMonthFilter}
          options={MONTH_OPTIONS}
          sx={filterFieldSx}
          minWidth={140}
        />
      )}
      {showProductFilter && showChannelFilter && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: MUTED, letterSpacing: '0.02em' }}>
            Filter
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={
              linkTypeFilter === 'ServiceNow' || isSnowLinkType(linkTypeFilter)
                ? 'ServiceNow'
                : (locationFilter.length === 1 ? locationFilter[0] : '')
            }
            onChange={(_, next) => {
              if (!next) {
                setLocationFilter([]);
                setLinkTypeFilter('all');
                return;
              }
              if (next === 'L1' || next === 'L2' || next === 'L3') {
                setLocationFilter([next]);
                setLinkTypeFilter('all');
                return;
              }
              setLocationFilter([]);
              setLinkTypeFilter(next);
            }}
            aria-label="Filter by L1, L2, L3, or ServiceNow"
            sx={pillGroupSx}
          >
            <ToggleButton value="L1" sx={pillToggleSx(LOCATION_STYLES.L1)}>
              L1
            </ToggleButton>
            <ToggleButton value="L2" sx={pillToggleSx(LOCATION_STYLES.L2)}>
              L2
            </ToggleButton>
            <ToggleButton value="L3" sx={pillToggleSx(LOCATION_STYLES.L3)}>
              L3
            </ToggleButton>
            <ToggleButton value="ServiceNow" sx={pillToggleSx(LINK_TYPE_STYLES.ServiceNow)}>
              Service
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={unassignedOnly}
            onChange={(e) => setUnassignedOnly(e.target.checked)}
            color="primary"
          />
        }
        label="Unassigned only"
        sx={{ ml: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: INK } }}
      />
      {showProductFilter && (
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={linkedOnly}
            onChange={(e) => setLinkedOnly(e.target.checked)}
            color="primary"
          />
        }
        label="Linked to L2 / L3 / ServiceNow"
        sx={{ ml: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: INK } }}
      />
      )}
      {showProductFilter && (
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={blockedOnly}
            onChange={(e) => setBlockedOnly(e.target.checked)}
            color="primary"
          />
        }
        label="Blocked (L1 / L2 / L3 / SNOW)"
        sx={{ ml: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: INK } }}
      />
      )}
      {ageLabel && (
        <Chip
          size="small"
          label={`Age · ${ageLabel}`}
          onDelete={onClearAge}
          sx={{ height: 28, fontWeight: 600, bgcolor: NEUTRAL_SURFACE, color: INK }}
        />
      )}
    </Box>
    <TableContainer sx={{ maxHeight: '70vh', overflowX: 'auto' }}>
      <Table size={showProductFilter ? 'medium' : 'small'} stickyHeader sx={{ width: '100%', tableLayout: 'auto' }}>
        <TableHead>
          <TableRow>
            {(tableHeaders).map((col) => (
              <TableCell
                key={col}
                sortDirection={showProductFilter && sortBy === col ? sortDir : false}
                title={(showProductFilter && SF_HEADER_LABEL[col]) || col}
                sx={showProductFilter
                  ? sfHeaderCellSx(col, isFullSfView)
                  : {
                    ...tableHeaderCellSx,
                    letterSpacing: '0.02em',
                    whiteSpace: 'nowrap',
                    ...(col === 'Subject' || col === 'Description'
                      ? { width: '100%' }
                      : { width: '1%', px: 1 })
                  }}
              >
                {showProductFilter ? (
                  <TableSortLabel
                    active={sortBy === col}
                    direction={sortBy === col ? sortDir : 'asc'}
                    onClick={() => handleSfSort(col)}
                    hideSortIcon={false}
                    sx={{
                      color: 'inherit',
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontSize: '0.875rem',
                      '&:hover': { color: DEEP_GREEN },
                      '&.Mui-active': { color: 'inherit' },
                      '& .MuiTableSortLabel-icon': {
                        color: 'inherit !important',
                        opacity: sortBy === col ? 1 : 0.45,
                        fontSize: '0.9rem'
                      }
                    }}
                  >
                    {SF_HEADER_LABEL[col] || col}
                  </TableSortLabel>
                ) : (
                  col
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow>
              <TableCell colSpan={tableHeaders.length}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  {(tickets || []).length === 0
                    ? emptyMessage
                    : (isSnowTable ? 'No incidents match these filters.' : 'No cases match these filters.')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {visible.map((ticket) => {
            const owner = ownerFromAssignedTo(ticket.assignedTo);
            const isSnowRow = isSnowLinkType(ticket.linkType);
            const snowLabel = snowCaseLabel(ticket) || (isSnowRow ? ticket.externalId : '') || '';
            const snowHref = ticket.snowUrl || (isSnowRow ? ticket.url : '');
            const snowPriKey = snowPriorityKey(ticketSnowPriority(ticket));
            const priorityStyle = useSnowPriority
              ? (SNOW_PRIORITY_STYLES[snowPriKey] || PRIORITY_STYLES[3])
              : (PRIORITY_STYLES[ticket.priority] || PRIORITY_STYLES[3]);
            const stateStyle = STATE_STYLES[ticket.state] || { bgcolor: NEUTRAL_SURFACE, color: INK };
            return (
              <TableRow
                key={ticket.id}
                hover
                // Salesforce case rows open the Ticket Journey; links and buttons inside keep their own action.
                onClick={showProductFilter ? (event) => {
                  if (event.target.closest('a, button, input')) return;
                  setJourneyTicket(ticket);
                } : undefined}
                sx={{
                  '&:nth-of-type(even)': { bgcolor: 'rgba(0,0,0,0.01)' },
                  '&:hover': { bgcolor: 'rgba(26,46,34,0.04)' },
                  ...(showProductFilter ? { cursor: 'pointer' } : null)
                }}
              >
                {showProductFilter ? (
                  <>
                    <TableCell sx={sfBodyCellSx('Case L1')}>
                      <CaseIdLink value={ticket.id} onClick={() => setCaseDetailsId(ticket.id)} color={L1_ACCENT} />
                    </TableCell>
                    {!isSnowSfView && (
                    <TableCell sx={sfBodyCellSx('Case L2')}>
                      <MultiTicketCell
                        ids={l2IdsOf(ticket)}
                        urlOf={(id) => ((ticket.l2Items || []).find((item) => String(item.id) === String(id)) || {}).url
                          || (String(id) === String(ticket.l2Id) ? ticket.l2Url : '')}
                        linkedId={linkedAdoIdOf(ticket)}
                        color={L2_ACCENT}
                        onOpen={setAdoDetails}
                      />
                    </TableCell>
                    )}
                    {!isL2SfView && !isSnowSfView && (
                    <TableCell sx={sfBodyCellSx('Case L3')}>
                      <MultiTicketCell
                        ids={ticket.l3Ids || []}
                        urlOf={(id) => ((ticket.l3Items || []).find((item) => String(item.id) === String(id)) || {}).url
                          || (String(id) === String((ticket.l3Ids || [])[0]) ? ticket.l3Url : '')}
                        linkedId={linkedAdoIdOf(ticket)}
                        color={L3_ACCENT}
                        onOpen={setAdoDetails}
                      />
                    </TableCell>
                    )}
                    {!isL2SfView && !isL3SfView && (
                    <TableCell sx={sfBodyCellSx('Case ServiceNow')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CaseIdLink
                          value={snowLabel}
                          href={snowHref}
                          color={INCIDENT_ACCENT}
                          onClick={SNOW_NUMBER.test(snowLabel) ? () => setSnowDetails({ number: snowLabel, url: snowHref }) : undefined}
                        />
                        {isSnowRow && (snowCaseLabel(ticket) || ticket.externalId) ? <LinkedMark /> : null}
                      </Box>
                    </TableCell>
                    )}
                    <TableCell
                      sx={{ ...sfBodyCellSx('Subject'), whiteSpace: 'normal' }}
                      title={ticket.title || ''}
                    >
                      {ticket.title}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...sfBodyCellSx('Age'),
                        fontWeight: 700,
                        color: INCIDENT_ACCENT
                      }}
                      title={ticket.createdDate ? new Date(ticket.createdDate).toLocaleString() : ''}
                    >
                      {formatTicketAge(ticket.createdDate)}
                    </TableCell>
                    <TableCell
                      sx={sfBodyCellSx('Created Date')}
                      title={ticket.createdDate ? new Date(ticket.createdDate).toLocaleString() : ''}
                    >
                      {formatSfTableDate(ticket.createdDate)}
                    </TableCell>
                    <TableCell
                      sx={sfBodyCellSx('Last Modified Date')}
                      title={ticket.lastModifiedDate ? new Date(ticket.lastModifiedDate).toLocaleString() : ''}
                    >
                      {formatSfTableDate(ticket.lastModifiedDate)}
                    </TableCell>
                    <TableCell sx={sfBodyCellSx('Salesforce Status')}>
                      {statusChip(ticket.state)}
                      {ticket.stage && (
                        <Box sx={{ mt: 0.5, fontSize: '0.7rem', color: MUTED }} title="Stage (ESC_STAGE_C)">
                          {ticket.stage}
                        </Box>
                      )}
                    </TableCell>
                    {isL2SfView && (
                    <TableCell sx={sfBodyCellSx('L2 Status')}>
                      {statusChip(adoLinkedStatusOf(ticket, 'l2State', EMPTY_L2_STATUS))}
                    </TableCell>
                    )}
                    {isL3SfView && (
                    <TableCell sx={sfBodyCellSx('L3 Status')}>
                      {statusChip(adoLinkedStatusOf(ticket, 'l3State', EMPTY_L3_STATUS))}
                    </TableCell>
                    )}
                    {isSnowSfView && (
                    <TableCell sx={sfBodyCellSx('ServiceNow Status')}>
                      {statusChip(ticket.snowState)}
                    </TableCell>
                    )}
                    {!isFullSfView && (
                    <TableCell sx={sfBodyCellSx('L1 Assignee')}>
                      <AssigneeCell
                        assignedTo={ticket.assignedTo}
                        color={L1_ACCENT}
                        bg={L1_BADGE_BG}
                        emptyLabel="Unassigned"
                      />
                    </TableCell>
                    )}
                    {isSnowSfView ? (
                    <TableCell sx={sfBodyCellSx('ServiceNow Assignee')}>
                      <AssigneeCell
                        assignedTo={ticket.snowAssignedTo}
                        color={INCIDENT_ACCENT}
                        bg={INCIDENT_BADGE_BG}
                      />
                    </TableCell>
                    ) : isFullSfView ? (
                    <TableCell sx={sfBodyCellSx('L2 Status')}>
                      {statusChip(ticket.l2State)}
                    </TableCell>
                    ) : (
                    <TableCell sx={sfBodyCellSx('L2 Assignee')}>
                      <AssigneeCell
                        assignedTo={ticket.l2AssignedTo}
                        color={L2_ACCENT}
                        bg={L2_BADGE_BG}
                      />
                    </TableCell>
                    )}
                    {!isL2SfView && !isSnowSfView && (isFullSfView ? (
                    <TableCell sx={sfBodyCellSx('L3 Status')}>
                      {statusChip(ticket.l3State)}
                    </TableCell>
                    ) : (
                    <TableCell sx={sfBodyCellSx('L3 Assignee')}>
                      <AssigneeCell
                        assignedTo={ticket.l3AssignedTo}
                        color={L3_ACCENT}
                        bg={L3_BADGE_BG}
                      />
                    </TableCell>
                    ))}
                    {isFullSfView && (
                    <TableCell sx={sfBodyCellSx('SNOW Status')}>
                      {statusChip(ticket.snowState)}
                    </TableCell>
                    )}
                    {isL2SfView ? (
                    <TableCell sx={sfBodyCellSx('Comments')}>
                      {ticket.l2Id ? (
                        <IconButton
                          size="small"
                          aria-label={`Comments for ${ticket.l2Id}`}
                          sx={{ color: accent }}
                          onClick={() => openComments(ticket, ticket.l2Id, [ticket.l2Id])}
                        >
                          <CommentIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      ) : (
                        <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>—</Typography>
                      )}
                    </TableCell>
                    ) : isSnowSfView ? null : (
                    <>
                    <TableCell sx={sfBodyCellSx('Version')}>
                      {(() => {
                        const version = ticket.adoFixVersion || ticket.fixVersion;
                        if (!(ticket.linkType === 'ADO' && ticket.adoId)) {
                          return (
                            <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>—</Typography>
                          );
                        }
                        return (
                          <Typography sx={{ fontSize: '0.75rem', color: version ? INK : MUTED }}>
                            {version || '—'}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    {isL3SfView && (
                    <TableCell sx={sfBodyCellSx('Comments')}>
                      {(() => {
                        const l3Id = (ticket.l3Ids && ticket.l3Ids[0]) || '';
                        const ids = [l3Id, ticket.l2Id].filter((id, index, list) => id && list.indexOf(id) === index);
                        if (ids.length === 0) {
                          return <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>—</Typography>;
                        }
                        return (
                          <IconButton
                            size="small"
                            aria-label={`Comments for ${ids[0]}`}
                            sx={{ color: accent }}
                            onClick={() => openComments(ticket, ids[0], ids)}
                          >
                            <CommentIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        );
                      })()}
                    </TableCell>
                    )}
                    </>
                    )}
                  </>
                ) : isSnowTable ? (
                  <>
                    <TableCell sx={{ width: '1%', px: 1 }}>
                      <CaseIdLink value={ticket.id} href={ticket.url} color={INCIDENT_ACCENT} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem', color: INK, width: '100%' }}>
                      {ticket.title || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ticket.state || '—'}
                        sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, ...stateStyle }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={snowPriorityLabel(ticketSnowPriority(ticket))}
                        sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, ...priorityStyle }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem', color: ticket.service ? INK : MUTED, whiteSpace: 'nowrap', px: 1 }}>
                      {ticket.service || '—'}
                    </TableCell>
                    <TableCell sx={{ width: '1%', px: 1, whiteSpace: 'nowrap' }}>
                      <AssigneeCell
                        assignedTo={ticket.assignedTo}
                        color={INCIDENT_ACCENT}
                        bg={INCIDENT_BADGE_BG}
                        emptyLabel="Unassigned"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem', color: MUTED, whiteSpace: 'nowrap', px: 1 }}>
                      {formatOpenedDay(ticket)}
                    </TableCell>
                  </>
                ) : (
                  <>
                <TableCell sx={{ width: '1%', px: 1, whiteSpace: 'nowrap' }}>
                  <CaseIdLink value={ticket.id} href={ticket.url} color={L1_ACCENT} />
                </TableCell>
                {showType && (
                  <TableCell sx={{ width: '1%', whiteSpace: 'nowrap', px: 1 }}>
                    <Chip
                      size="small"
                      label={TYPE_LABELS[ticket.workItemType] || ticket.workItemType || '—'}
                      sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, bgcolor: badgeBg, color: accent }}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ fontSize: '0.8125rem', color: INK, width: '100%' }}>
                  {ticket.title}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={ticket.state || '—'}
                    sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, ...stateStyle }}
                  />
                </TableCell>
                <TableCell sx={{ width: '1%', whiteSpace: 'nowrap', px: 1 }}>
                  {(() => {
                    const version = ticket.adoFixVersion || ticket.fixVersion;
                    return (
                      <Typography sx={{ fontSize: '0.8125rem', color: version ? INK : MUTED }}>
                        {version || '—'}
                      </Typography>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={useSnowPriority ? snowPriorityLabel(ticketSnowPriority(ticket)) : (PRIORITY_LABELS[ticket.priority] || '—')}
                    sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, ...priorityStyle }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: badgeBg,
                        color: accent,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      {owner.initials}
                    </Box>
                    <Typography sx={{ fontSize: '0.8125rem', color: INK, whiteSpace: 'nowrap' }}>
                      {owner.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ width: '1%', px: 1, whiteSpace: 'nowrap' }}>
                  {(() => {
                    const ids = ticket.id ? [String(ticket.id)] : [];
                    if (ids.length === 0) {
                      return <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>—</Typography>;
                    }
                    return (
                      <IconButton
                        size="small"
                        aria-label={`Comments for ${ids[0]}`}
                    sx={{ color: accent }}
                        onClick={() => openComments(ticket, ids[0], ids)}
                      >
                        <CommentIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                    );
                  })()}
                </TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    <TablePagination
      component="div"
      count={filtered.length}
      page={page}
      onPageChange={(_, next) => setPage(next)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
      }}
      rowsPerPageOptions={[100, 500, 1000, { value: -1, label: 'All' }]}
      sx={{
        borderTop: '1px solid rgba(26,46,34,0.08)',
        color: MUTED,
        '& .MuiTablePagination-toolbar': { minHeight: 52 }
      }}
    />
    {showProductFilter && (
      <>
        <TicketJourneyDialog open={Boolean(journeyTicket)} onClose={() => setJourneyTicket(null)} ticket={journeyTicket} />
        {caseDetailsId && <CaseDetailsDialog caseNumber={caseDetailsId} onClose={() => setCaseDetailsId(null)} />}
        {snowDetails && (
          <SnowTicketDetailsDialog
            number={snowDetails.number}
            fallbackUrl={snowDetails.url}
            color={INCIDENT_ACCENT}
            onClose={() => setSnowDetails(null)}
          />
        )}
        {adoDetails && (
          <AdoItemDetailsDialog
            workItemId={adoDetails.id}
            fallbackUrl={adoDetails.url}
            color={adoDetails.color}
            onClose={() => setAdoDetails(null)}
          />
        )}
      </>
    )}
    <Dialog
      open={Boolean(commentTicket)}
      onClose={closeComments}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: '80vh' } }}
    >
      <DialogTitle>
        {commentTicket
          ? `Comments · ${commentTicket.location || 'ADO'} ${commentTicket.adoId}`
          : 'Comments'}
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', minHeight: 280 }}>
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 120, mb: 2 }}>
          {commentsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!commentsLoading && commentsError && <Alert severity="error">{commentsError}</Alert>}
          {!commentsLoading && !commentsError && comments.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No discussion comments on this work item.
            </Typography>
          )}
          {!commentsLoading && comments.map((comment) => (
            <Box key={`${comment.workItemId}-${comment.id}`} sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: INK }}>
                {comment.author || 'Unknown'}
                {comment.workItemId ? ` · ${comment.workItemId}` : ''}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: MUTED, mb: 0.5 }}>
                {comment.date ? new Date(comment.date).toLocaleString() : ''}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: INK, whiteSpace: 'pre-wrap' }}>
                {comment.text}
              </Typography>
              <Divider sx={{ mt: 1.5 }} />
            </Box>
          ))}
        </Box>
        <Autocomplete
          multiple
          filterSelectedOptions
          filterOptions={(options) => options}
          options={identityOptions}
          value={commentMentions}
          inputValue={identityInput}
          loading={identityLoading}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          getOptionLabel={(option) => option.displayName || ''}
          onChange={handleMentionsChange}
          onInputChange={(_event, value, reason) => {
            setIdentityInput(value);
            if (reason === 'input') searchIdentities(value);
          }}
          noOptionsText={
            identityLoading
              ? 'Searching…'
              : identityInput.trim().length < 2
                ? 'Type at least 2 letters'
                : (identityError || 'No matching people in ADO')
          }
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography sx={{ fontSize: '0.875rem' }}>{option.displayName}</Typography>
                {option.mail ? (
                  <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>{option.mail}</Typography>
                ) : null}
              </Box>
            </li>
          )}
          renderTags={(value, getTagProps) => value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              size="small"
              label={`@${option.displayName}`}
              sx={{ maxWidth: 240 }}
            />
          ))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Notify people"
              placeholder="Search names to @mention"
              helperText="They get an ADO notification on this work item"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {identityLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          sx={{ mb: 1.5 }}
        />
        <TextField
          label="Add a comment"
          placeholder="Write a comment to post on the ADO ticket"
          value={commentDraft}
          onChange={(event) => setCommentDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && canPostComment) {
              event.preventDefault();
              handlePostComment();
            }
          }}
          multiline
          minRows={3}
          fullWidth
          disabled={commentPosting}
        />
        {commentPostError && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{commentPostError}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        {absoluteHttpUrl(commentTicket?.url) && (
          <Button
            href={absoluteHttpUrl(commentTicket.url)}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
            sx={{ textTransform: 'none', mr: 'auto' }}
          >
            Open work item
          </Button>
        )}
        <Button
          onClick={closeComments}
          sx={{ textTransform: 'none' }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handlePostComment}
          disabled={!canPostComment}
          sx={{
            textTransform: 'none',
            bgcolor: accent,
            '&:hover': { bgcolor: accent, filter: 'brightness(0.92)' }
          }}
        >
          {commentPosting ? 'Posting…' : 'Post to ADO'}
        </Button>
      </DialogActions>
    </Dialog>
  </Paper>
  );
});

const cardSurfaceSx = {
  p: '20px 24px',
  borderRadius: '12px',
  border: '1px solid rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  height: 'auto',
  minHeight: '100%',
  boxSizing: 'border-box',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
  transition: 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 10px rgba(0,0,0,0.08), 0 3px 6px rgba(0,0,0,0.06)'
  }
};

const boardChartRowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 0.9fr) minmax(0, 1.3fr)' },
  gap: 2,
  alignItems: 'start',
  mb: 0
};

const NO_STAGE_LABEL = '(No stage)';
const L1_BREAKDOWN_STORAGE_KEY = 'l1OpenBreakdown';

const readL1Breakdown = () => {
  try {
    return window.localStorage.getItem(L1_BREAKDOWN_STORAGE_KEY) === 'stage' ? 'stage' : 'state';
  } catch (_err) {
    return 'state';
  }
};

/** Filters the L1 sync applied to the Salesforce cases, with Case Owner names. Re-read on every open (owners are configurable). */
const L1FiltersDialog = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setData(null);
    setError(null);
    authGet('/api/customer-issues/lakehouse/filters', null, 'json', { useCache: false })
      .then((res) => {
        if (cancelled) return;
        if (!res.success) throw new Error(res.message || 'Could not load the filters.');
        setData(res);
      })
      .catch((err) => { if (!cancelled) setError((err && err.message) || 'Could not load the filters.'); });
    return () => { cancelled = true; };
  }, [open]);
  const row = (label, value) => (
    <Box sx={{ display: 'flex', gap: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ fontWeight: 700, width: 130, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" component="div">{value}</Typography>
    </Box>
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>L1 Open filters</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!data && !error && <CircularProgress size={24} />}
        {data && (
          <>
            {row('Source', data.table)}
            {row('Status', (data.openStatuses || []).join(', '))}
            {row('Brand', data.brand)}
            {row('Case owners', (
              <>
                {(data.caseOwners || []).length ? data.caseOwners.join(', ') : 'None configured'}
                {data.includesBlankOwner && <div>plus cases with no owner</div>}
                {(data.unresolvedOwners || []).length > 0 && (
                  <div>Not applied (no OWNER_ID yet): {data.unresolvedOwners.join(', ')}</div>
                )}
              </>
            ))}
            {row('Not deleted', 'Salesforce IsDeleted and Fivetran-deleted rows are excluded')}
            {row('Closed cases', `${(data.closedStatuses || []).join(', ')} count in the monthly chart only, for ${(data.chartYears || []).join(', ')}`)}
            {data.sql && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Open backlog WHERE clause (owners shown as OWNER_ID)</Typography>
                <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{data.sql}</Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

/** L1 Open card in the same look as the Production Bugs / Platform Investigation cards on the L3 tabs. */
const L1OverviewCard = ({ open, newLast24h, rows, stageRows, stageAvailable }) => {
  const [breakdown, setBreakdown] = useState(readL1Breakdown);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const handleBreakdown = (_event, next) => {
    if (!next) return;
    setBreakdown(next);
    try { window.localStorage.setItem(L1_BREAKDOWN_STORAGE_KEY, next); } catch (_err) { /* per-viewer convenience only */ }
  };
  // stageRows is null while the case list loads. A saved Stage choice then waits ("Loading…") instead of
  // flashing State. When no case has a stage yet (sync not run), Stage is disabled and State is shown.
  const stageLoading = breakdown === 'stage' && stageRows == null;
  const stageUsable = Array.isArray(stageRows) && stageAvailable;
  const byStage = breakdown === 'stage' && (stageLoading || stageUsable);
  const shownRows = stageLoading ? [] : (byStage ? stageRows : rows);
  const stageUnsynced = Array.isArray(stageRows) && !stageAvailable;
  return (
  <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.5,
      background: `linear-gradient(135deg, ${WARN_ORANGE}ee 0%, ${WARN_ORANGE}bb 100%)`
    }}>
      <SalesforceIcon sx={{ color: '#fff' }} />
      <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 0.3 }}>
        L1 Open · Salesforce
      </Typography>
      {hasPermission('system:admin') && (
        <>
          <Button
            size="small"
            onClick={() => setFiltersOpen(true)}
            sx={{ ml: 'auto', color: '#fff', textTransform: 'none', textDecoration: 'underline', minWidth: 0, py: 0, '&:hover': { textDecoration: 'underline', bgcolor: 'rgba(255,255,255,0.15)' } }}
          >
            filters
          </Button>
          <L1FiltersDialog open={filtersOpen} onClose={() => setFiltersOpen(false)} />
        </>
      )}
    </Box>
    <Box sx={{ p: 2.5, flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
        <MuiTooltip
          arrow
          placement="top"
          title={(
            <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
              <div><b>Open + New + WIP</b>: Salesforce cases in an open, new or work-in-progress state. Matches the L1 Open tile.</div>
              <div><b>New in last 24h</b>: those open cases opened in the last 24 hours.</div>
              <div><b>Open by state / stage</b>: open cases grouped by current Salesforce state, or by ESC_STAGE_C when Stage is selected.</div>
            </Box>
          )}
        >
          <IconButton size="small"><InfoIcon fontSize="small" color="action" /></IconButton>
        </MuiTooltip>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-around', pb: 2 }}>
        <Box sx={{ textAlign: 'center', px: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: WARN_ORANGE, lineHeight: 1 }}>{open ?? '—'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontWeight: 500 }}>
            Open + New + WIP
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', px: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#555', lineHeight: 1 }}>{newLast24h ?? '—'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontWeight: 500 }}>
            New in last 24h
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary' }}>
          {byStage ? 'OPEN BY STAGE' : 'OPEN BY STATE'}
        </Typography>
        <Box title={stageUnsynced ? 'Stage is not available yet — it fills in after the next Salesforce sync.' : undefined}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={byStage ? 'stage' : 'state'}
            onChange={handleBreakdown}
            aria-label="Open cases breakdown"
            sx={{ '& .MuiToggleButton-root': { py: 0, px: 1, fontSize: '0.7rem', textTransform: 'none', lineHeight: 1.8 } }}
          >
            <ToggleButton value="state">State</ToggleButton>
            <ToggleButton value="stage" disabled={stageUnsynced}>Stage</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        {/* Many stages can exist: scroll the list, keep the total pinned below it. */}
        <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
          {stageLoading && (
            <Box sx={{ px: 1.5, py: 0.6, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">Loading…</Typography>
            </Box>
          )}
          {(shownRows || []).map((row) => (
            <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.6, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption">{row.label}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: WARN_ORANGE }}>{row.value}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.6, bgcolor: '#f5f5f5' }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Open Total</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: WARN_ORANGE }}>{open ?? '—'}</Typography>
        </Box>
      </Box>
    </Box>
  </Paper>
  );
};

const StatBoardCard = ({
  badge,
  category,
  hero,
  heroLabel,
  rows,
  adoUrl,
  boardButtonLabel = 'Open ADO board',
  showBoardButton = false,
  accent = L2_ACCENT,
  badgeBg = L2_BADGE_BG,
  compact = false
}) => (
  <Paper
    elevation={0}
    sx={{
      ...cardSurfaceSx,
      height: compact ? '100%' : 'auto',
      minHeight: compact ? '100%' : 'auto',
      borderTop: `3px solid ${accent}`,
      ...(compact ? { p: '12px 14px' } : {})
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: compact ? 1 : 1.5, gap: 0.75 }}>
      <Box
        sx={{
          px: compact ? 1 : 1.25,
          py: 0.25,
          borderRadius: '8px',
          bgcolor: badgeBg,
          color: accent,
          fontSize: compact ? '0.6875rem' : '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.02em',
          lineHeight: 1.6
        }}
      >
        {badge}
      </Box>
      <Typography sx={{ color: MUTED, fontSize: compact ? '0.75rem' : '0.875rem', fontWeight: 500, textAlign: 'right' }}>
        {category}
      </Typography>
    </Box>

    <Typography sx={{ fontWeight: 700, color: INK, fontSize: compact ? '1.5rem' : '2.25rem', lineHeight: 1.1 }}>
      {hero ?? '—'}
    </Typography>
    <Typography sx={{ color: MUTED, fontSize: compact ? '0.75rem' : '0.875rem', mt: 0.5, mb: (rows && rows.length) || showBoardButton ? (compact ? 1 : 1.5) : 0 }}>
      {heroLabel}
    </Typography>

    {rows && rows.length > 0 && (
    <>
    <Divider sx={{ mb: compact ? 1 : 1.5 }} />

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0.6 : 1, flex: 1 }}>
      {rows.map((row, index) => (
        row.divider ? (
          <Divider key={`div-${index}`} sx={{ my: 0.5 }} />
        ) : (
          <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              {row.color && (
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: row.color, flexShrink: 0 }} />
              )}
              <Typography
                noWrap={compact}
                sx={{ color: row.color || MUTED, fontSize: compact ? '0.75rem' : '0.875rem', fontWeight: row.color ? 600 : 400, lineHeight: 1.3 }}
              >
                {row.label}
              </Typography>
        </Box>
            <Typography sx={{ color: row.color || INK, fontWeight: 700, fontSize: compact ? '0.8125rem' : '0.9375rem', flexShrink: 0 }}>
              {row.value ?? '—'}
            </Typography>
          </Box>
        )
      ))}
    </Box>
    </>
    )}

    {showBoardButton && absoluteHttpUrl(adoUrl) && (
    <Button
      fullWidth
      variant="outlined"
      href={absoluteHttpUrl(adoUrl)}
      target="_blank"
      rel="noopener noreferrer"
      endIcon={<OpenInNewIcon sx={{ fontSize: compact ? 14 : 16 }} />}
      onClick={(event) => event.stopPropagation()}
      sx={{
        mt: 'auto',
        pt: compact ? 1.25 : 2,
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.02em',
        fontSize: compact ? '0.75rem' : undefined,
        color: accent,
        borderColor: accent,
        borderRadius: '10px',
        py: compact ? 0.5 : 0.75,
        minWidth: 0,
        whiteSpace: compact ? 'nowrap' : undefined,
        '&:hover': {
          borderColor: accent,
          bgcolor: badgeBg
        }
      }}
    >
      {boardButtonLabel}
    </Button>
    )}
  </Paper>
);

/**
 * Age-of-open-tickets breakdown, bucketed via AGE_BUCKETS/ageBucketKey.
 * Buckets are clickable: selecting one drives the paired TicketBacklogTable's
 * `ageBucket` prop (which filters its rows and shows a clearable chip via
 * `onClearAge`) so the card and table stay in sync.
 */
const AgeBreakdownCard = ({
  tickets = [],
  selectedBucket = 'all',
  onSelectBucket,
  title = 'Age of open tickets',
  accent = L2_ACCENT
}) => {
  const counts = useMemo(() => {
    const map = {};
    AGE_BUCKETS.forEach((bucket) => { map[bucket.key] = 0; });
    (tickets || []).forEach((ticket) => {
      const key = ageBucketKey(ticket.createdDate);
      if (key && map[key] != null) map[key] += 1;
    });
    return map;
  }, [tickets]);

  const total = useMemo(
    () => Object.values(counts).reduce((sum, n) => sum + n, 0),
    [counts]
  );

  const oldestDays = useMemo(() => {
    let max = null;
    (tickets || []).forEach((ticket) => {
      const days = daysOpen(ticket.createdDate);
      if (days != null && (max == null || days > max)) max = days;
    });
    return max;
  }, [tickets]);

  return (
    <Paper elevation={0} sx={{ ...cardSurfaceSx, borderTop: `3px solid ${accent}` }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
        <Typography sx={{ fontWeight: 700, color: INK, fontSize: '0.9375rem' }}>{title}</Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
          {total} open{oldestDays != null ? ` · oldest ${Math.floor(oldestDays)}d` : ''}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {AGE_BUCKETS.map((bucket) => {
          const count = counts[bucket.key] || 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const selected = selectedBucket === bucket.key;
          const activate = () => {
            if (onSelectBucket) onSelectBucket(selected ? 'all' : bucket.key);
          };
          return (
            <Box
              key={bucket.key}
              role="button"
              tabIndex={onSelectBucket ? 0 : -1}
              aria-pressed={selected}
              onClick={activate}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  activate();
                }
              }}
              sx={{
                cursor: onSelectBucket ? 'pointer' : 'default',
                borderRadius: '8px',
                p: 0.75,
                outline: selected ? `2px solid ${bucket.color}` : 'none',
                outlineOffset: '-2px',
                bgcolor: selected ? bucket.track : 'transparent',
                '&:hover': onSelectBucket ? { bgcolor: bucket.track } : undefined
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.8125rem', color: INK, fontWeight: 600 }}>{bucket.label}</Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: MUTED }}>{count}</Typography>
              </Box>
              <Box sx={{ height: 6, borderRadius: '3px', bgcolor: bucket.track, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: bucket.color, borderRadius: '3px' }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const OperationalExcellenceHub = () => {
  const [l1Stats, setL1Stats] = useState(null);
  const [l1Tickets, setL1Tickets] = useState([]);
  const [l1Loading, setL1Loading] = useState(true);
  const [l1Error, setL1Error] = useState(null);
  const [l1ImportMessage, setL1ImportMessage] = useState(null);
  const [l1ImportError, setL1ImportError] = useState(false);
  const [l1LakeSyncing, setL1LakeSyncing] = useState(false);
  const [lastLakeSync, setLastLakeSync] = useState(null);
  const [l1TicketsReady, setL1TicketsReady] = useState(false);
  const mountedRef = useRef(true);
  const lakehouseSyncInFlightRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [l2Stats, setL2Stats] = useState(null);
  const [l2Summary, setL2Summary] = useState(null);
  const [l2Tickets, setL2Tickets] = useState([]);
  const [l3Stats, setL3Stats] = useState(null);
  const [l3Summary, setL3Summary] = useState(null);
  const [l3Tickets, setL3Tickets] = useState([]);
  const [l2Loading, setL2Loading] = useState(true);
  const [l2Error, setL2Error] = useState(null);
  const [l3Loading, setL3Loading] = useState(true);
  const [l3Error, setL3Error] = useState(null);
  const INITIAL_OE_CHAIN = 'topCustomers';
  const [chainLevel, setChainLevel] = useState(INITIAL_OE_CHAIN);
  // Keep Top Customers mounted after first visit (Overview-style). Matches default tab.
  const [topCustomersVisited, setTopCustomersVisited] = useState(INITIAL_OE_CHAIN === 'topCustomers');
  // Open (New/Open/WIP) cases across all top customers, reported by TopCustomersPage once it loads.
  const [topCustomersOpen, setTopCustomersOpen] = useState(null);
  // Bumped after a Sync so TopCustomersPage reloads.
  const [topCustomersRefreshKey, setTopCustomersRefreshKey] = useState(0);
  const [topCustomersFailed, setTopCustomersFailed] = useState(false);
  // Customers left out of the open count because they failed to load; the tile then shows "N+".
  const [topCustomersExcluded, setTopCustomersExcluded] = useState(0);
  const [sfOverviewTab, setSfOverviewTab] = useState('salesforce');
  const [selectedTile, setSelectedTile] = useState('l1Open');
  const activeSfOverviewTab = SF_OVERVIEW_TABS.some((tab) => tab.id === sfOverviewTab)
    ? sfOverviewTab
    : 'salesforce';
  // Per-tab age-bucket filter, driven by AgeBreakdownCard below and consumed
  // by TicketBacklogTable's own `ageBucket`/`onClearAge` props.
  const [l1AgeBucket, setL1AgeBucket] = useState('all');
  const [ageBucket, setAgeBucket] = useState('all');
  const [l3AgeBucket, setL3AgeBucket] = useState('all');
  const [snowAgeBucket, setSnowAgeBucket] = useState('all');
  const [snowTickets, setSnowTickets] = useState([]);
  const [snowInstanceUrl, setSnowInstanceUrl] = useState('');
  const [snowLoading, setSnowLoading] = useState(true);
  const [snowError, setSnowError] = useState(null);
  const clearL1Age = useCallback(() => setL1AgeBucket('all'), []);
  const clearAge = useCallback(() => setAgeBucket('all'), []);
  const clearL3Age = useCallback(() => setL3AgeBucket('all'), []);
  const clearSnowAge = useCallback(() => setSnowAgeBucket('all'), []);

  const loadL1Stats = ({ silent = false } = {}) => {
    if (!silent) {
      setL1Error(null);
      setL1Loading(true);
    }
    // Backlog drives KPIs + case list. Stats (boardMonthly) is chart-only and must not gate them.
    const backlogReq = authGet('/api/customer-issues/backlog', null, 'json', { useCache: false })
      .then((backlogRes) => {
        if (!mountedRef.current) return;
        setL1Tickets(backlogRes.tickets || []);
        setL1TicketsReady(true);
      });
    const statsReq = authGet('/api/customer-issues/stats', null, 'json', { useCache: false })
      .then((statsRes) => {
        if (!mountedRef.current) return;
        const stats = statsRes.stats || null;
        setL1Stats(stats);
        if (stats && stats.lastLakeSync) setLastLakeSync(stats.lastLakeSync);
      })
      .finally(() => {
        if (mountedRef.current && !silent) setL1Loading(false);
      });
    return Promise.all([backlogReq, statsReq])
      .catch((err) => {
        if (!mountedRef.current) return;
        if (silent) {
          console.warn('[OperationalExcellenceHub] Silent L1 refresh failed:', err.message);
        } else {
          setL1Error(err.message || 'Failed to load Salesforce ticket stats');
        }
      })
      .finally(() => {
        if (mountedRef.current && !silent) setL1Loading(false);
      });
  };

  const handleLakehouseSync = async ({ quiet = false } = {}) => {
    if (lakehouseSyncInFlightRef.current) {
      if (!quiet) setL1ImportMessage('Sync already running…');
      return;
    }
    lakehouseSyncInFlightRef.current = true;
    if (!quiet) {
      setL1LakeSyncing(true);
      setL1ImportMessage(null);
      setL1ImportError(false);
    }
    try {
      const result = await authPost(
        '/api/customer-issues/lakehouse/sync',
        { force: !quiet },
        { timeout: 180000, invalidateCache: '/api/customer-issues' }
      );
      if (!mountedRef.current) return;
      if (result.lastSync) setLastLakeSync(result.lastSync);
      if (!quiet) {
        setL1LakeSyncing(false);
        if (result.skipped && result.reason === 'in-flight') {
          setL1ImportMessage('Sync already running…');
        } else {
          setL1ImportMessage(result.message || `Synced ${result.imported} HighQ cases from Data Lake.`);
          setL1ImportError(false);
        }
      }
      if (!result.skipped) {
        // Hero/overview prefer l1Tickets once they have loaded. Leave those
        // stale and L1 Open stays at the pre-sync number (427) while backlog
        // re-fetches 19k rows. Drop tickets first so the KPI uses stats.
        setL1TicketsReady(false);
        setL1Tickets([]);
        // The sync also refreshed Top Customers and the Zendesk links, so every number on the page reloads, not
        // just L1: the Vital Signs tiles, the L2/L3 boards, and Top Customers (its tile and age card).
        setVsBuckets(null);
        setVsBucketsFailed(false);
        if (topCustomersVisited) setTopCustomersOpen(null);
        setTopCustomersRefreshKey((key) => key + 1);
        await Promise.all([
          loadL1Stats({ silent: false }),
          loadVsBuckets(true),
          loadL2Data(true),
          loadL3Data(true)
        ]);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      const raw = err.message || '';
      const unreachable = /failed to fetch|networkerror|load failed|err_connection/i.test(raw);
      setL1ImportMessage(
        unreachable
          ? 'Failed to fetch: the Metrics Portal API did not respond. The Data Lake was not changed. Confirm the API is running on port 5001, then retry Sync.'
          : (raw || 'Failed to sync from Data Lake')
      );
      setL1ImportError(true);
    } finally {
      lakehouseSyncInFlightRef.current = false;
      if (mountedRef.current) setL1LakeSyncing(false);
    }
  };

  // `fresh` skips the client cache (used after a Sync, so the boards show the newly synced data).
  const loadL2Data = (fresh = false) => {
    const options = fresh ? { useCache: false } : {};
    return Promise.all([
      authGet('/api/l2-investigation/stats', null, 'json', options)
        .then((statsRes) => {
          if (!mountedRef.current) return;
          setL2Stats(statsRes.stats || []);
          setL2Summary(statsRes.summary || null);
          setL2Error(null);
          setL2Loading(false);
        })
        .catch((err) => {
          if (mountedRef.current) {
            setL2Error(err.message || 'Failed to load L2 investigation stats');
            setL2Loading(false);
          }
        }),
      authGet('/api/l2-investigation/backlog', null, 'json', options)
        .then((backlogRes) => {
          if (!mountedRef.current) return;
          setL2Tickets(backlogRes.tickets || []);
        })
        .catch((err) => {
          if (mountedRef.current) setL2Error(err.message || 'Failed to load L2 investigation stats');
        })
    ]);
  };

  const loadL3Data = (fresh = false) => {
    const options = fresh ? { useCache: false } : {};
    return Promise.all([
      authGet('/api/l3-engineering/stats', null, 'json', options)
        .then((statsRes) => {
          if (!mountedRef.current) return;
          setL3Stats(statsRes.stats || []);
          setL3Summary(statsRes.summary || null);
          setL3Error(null);
          setL3Loading(false);
        })
        .catch((err) => {
          if (mountedRef.current) {
            setL3Error(err.message || 'Failed to load L3 engineering stats');
            setL3Loading(false);
          }
        }),
      authGet('/api/l3-engineering/backlog', null, 'json', options)
        .then((backlogRes) => {
          if (!mountedRef.current) return;
          setL3Tickets(backlogRes.tickets || []);
        })
        .catch((err) => {
          if (mountedRef.current) setL3Error(err.message || 'Failed to load L3 engineering stats');
        })
    ]);
  };

  useEffect(() => {
    loadL1Stats();
    loadL2Data();
    loadL3Data();

    authGet('/api/servicenow/config')
      .then((res) => {
        if (!mountedRef.current) return;
        const url = res && res.config && res.config.instanceUrl
          ? String(res.config.instanceUrl).replace(/\/+$/, '')
          : '';
        setSnowInstanceUrl(url);
      })
      .catch((err) => {
        console.warn('[OperationalExcellenceHub] Failed to load ServiceNow config:', err.message);
      });

    getServiceNowTickets({ page: 1, limit: 2000 })
      .then((res) => {
        if (!mountedRef.current) return;
        setSnowTickets(res.tickets || []);
        setSnowLoading(false);
      })
      .catch((err) => {
        if (mountedRef.current) {
          setSnowError(err.message || 'Failed to load ServiceNow tickets');
          setSnowLoading(false);
        }
      });
  }, []);

  const l1OpenTickets = useMemo(
    () => (l1Tickets || []).filter((ticket) => isSfAllBoardState(ticket.state)),
    [l1Tickets]
  );
  // Same as L2/L3: monthly series from stats payload, not a second API call.
  const l1Monthly = Array.isArray(l1Stats?.boardMonthly) ? l1Stats.boardMonthly : EMPTY_SERIES;
  const l1L2AdoTickets = useMemo(
    () => (l1Tickets || []).filter((ticket) => isL2OnlyLink(ticket) && isLinkedAdoRow(ticket, 'l2State')),
    [l1Tickets]
  );
  const l1L3AdoTickets = useMemo(
    () => (l1Tickets || []).filter((ticket) => isL3Linked(ticket) && isLinkedAdoRow(ticket, 'l3State')),
    [l1Tickets]
  );
  const l1SnowTickets = useMemo(
    () => (l1Tickets || []).filter((ticket) => isSnowLinkType(ticket.linkType) && isSfHotOpenState(ticket.state)),
    [l1Tickets]
  );
  const portalSnowTickets = useMemo(
    () => mapPortalSnowTickets(snowTickets, snowInstanceUrl),
    [snowTickets, snowInstanceUrl]
  );
  const portalSnowOpenTickets = useMemo(
    () => portalSnowTickets.filter((ticket) => !isSnowClosedState(ticket.state)),
    [portalSnowTickets]
  );
  const snowMonthly = useMemo(
    () => monthlyFromTickets(
      portalSnowTickets,
      (ticket) => isSnowClosedState(ticket.state),
      (ticket) => ticket.resolvedDate
    ),
    [portalSnowTickets]
  );
  const sfOverviewBoard = useMemo(() => {
    if (activeSfOverviewTab === 'l2ado') {
      return {
        tickets: l1L2AdoTickets,
        title: 'Salesforce · linked to L2',
        accent: L2_ACCENT,
        badgeBg: L2_BADGE_BG,
        emptyMessage: 'No Salesforce cases linked to L2.'
      };
    }
    if (activeSfOverviewTab === 'l3ado') {
      return {
        tickets: l1L3AdoTickets,
        title: 'Salesforce · linked to L3',
        accent: L3_ACCENT,
        badgeBg: L3_BADGE_BG,
        emptyMessage: 'No Salesforce cases linked to L3.'
      };
    }
    if (activeSfOverviewTab === 'snow') {
      return {
        tickets: l1SnowTickets,
        title: 'Salesforce · linked to ServiceNow',
        accent: INCIDENT_ACCENT,
        badgeBg: INCIDENT_BADGE_BG,
        emptyMessage: 'No Salesforce cases linked to ServiceNow.'
      };
    }
    return {
      tickets: l1OpenTickets,
      title: 'Salesforce',
      accent: L1_ACCENT,
      badgeBg: L1_BADGE_BG,
      emptyMessage: 'No Salesforce cases.'
    };
  }, [activeSfOverviewTab, l1OpenTickets, l1L2AdoTickets, l1L3AdoTickets, l1SnowTickets]);

  const l1Board = useMemo(() => {
    const counts = l1TicketsReady
      ? countMatchingStatuses(l1Tickets, isSfHotOpenState)
      : countMatchingStatusesFromMap(l1Stats?.statusCounts, isSfHotOpenState);
    const total = l1TicketsReady
      ? sumStatusCounts(counts)
      : ((l1Stats?.newCount || 0) + (l1Stats?.openCount || 0) + (l1Stats?.wipCount || 0));
    return {
      badge: 'L1',
      category: 'Salesforce',
      hero: total,
      heroLabel: 'Open + New + WIP',
      rows: statusRowsFromCounts(counts),
      accent: L1_ACCENT,
      badgeBg: L1_BADGE_BG
    };
  }, [l1Stats, l1Tickets, l1TicketsReady]);

  // Open L1 cases grouped by Salesforce ESC_STAGE_C, biggest first; blank stages are counted last.
  const l1StageRows = useMemo(() => {
    if (!l1TicketsReady) return null;
    const counts = new Map();
    (l1Tickets || []).filter((ticket) => isSfHotOpenState(ticket.state)).forEach((ticket) => {
      const label = String(ticket.stage || '').trim() || NO_STAGE_LABEL;
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => (a.label === NO_STAGE_LABEL) - (b.label === NO_STAGE_LABEL) || b.value - a.value);
  }, [l1Tickets, l1TicketsReady]);

  // False until a sync has stored a stage on at least one open case (old cached cases have none).
  const l1StageAvailable = useMemo(
    () => (l1Tickets || []).some((ticket) => isSfHotOpenState(ticket.state) && String(ticket.stage || '').trim()),
    [l1Tickets]
  );

  const snowBoard = useMemo(() => {
    const counts = countTicketStatuses(portalSnowTickets);
    const rows = snowStatusRowsFromCounts(counts);
    const hero = rows.reduce((sum, row) => sum + (row.value || 0), 0);
    return {
      badge: 'ServiceNow',
      category: 'Major Incidents',
      hero: snowLoading ? '—' : hero,
      heroLabel: 'New + In Progress + Assigned',
      rows,
      adoUrl: snowInstanceUrl || l1Stats?.snowBoardUrl || '',
      accent: INCIDENT_ACCENT,
      badgeBg: INCIDENT_BADGE_BG
    };
  }, [portalSnowTickets, snowLoading, snowInstanceUrl, l1Stats]);

  const overviewSnowBoard = useMemo(() => {
    const { hero, rows } = linkedSnowBoardFromCases(l1SnowTickets);
    return {
      badge: 'ServiceNow',
      category: 'Major Incidents',
      hero: l1TicketsReady ? hero : '—',
      heroLabel: 'Salesforce linked to ServiceNow',
      rows,
      accent: INCIDENT_ACCENT,
      badgeBg: INCIDENT_BADGE_BG
    };
  }, [l1SnowTickets, l1TicketsReady]);

  const [vsBuckets, setVsBuckets] = useState(null);
  // True when the buckets could not be loaded, so the tiles say "Error" instead of staying at "—".
  const [vsBucketsFailed, setVsBucketsFailed] = useState(false);
  const loadVsBuckets = (fresh = false) => authGet(
    '/api/customer-issues/vital-signs-buckets', null, 'json', fresh ? { useCache: false } : {}
  )
    .then((res) => {
      if (!mountedRef.current) return;
      if (res && res.success) {
        setVsBuckets(res.buckets);
        setVsBucketsFailed(false);
      } else {
        setVsBucketsFailed(true);
      }
    })
    .catch((err) => {
      console.warn('[OperationalExcellenceHub] Vital Signs buckets failed:', err.message);
      if (mountedRef.current) setVsBucketsFailed(true);
    });
  useEffect(() => {
    loadVsBuckets();
  }, []);

  const l1OpenValue = l1TicketsReady
    ? sumStatusCounts(countMatchingStatuses(l1Tickets, isSfHotOpenState))
    : ((l1Stats?.newCount || 0) + (l1Stats?.openCount || 0) + (l1Stats?.wipCount || 0));

  const tileValue = (key) => {
    if (key === 'topCustomers') {
      if (topCustomersOpen == null) return topCustomersFailed ? 'Error' : '—';
      return topCustomersExcluded > 0 ? `${topCustomersOpen}+` : topCustomersOpen;
    }
    if (key === 'l1Open') return (l1Stats == null && !l1TicketsReady && l1Loading) ? '—' : l1OpenValue;
    // A count the server could not compute comes back null (shown as "—", like a count still loading); buckets
    // that failed to load at all show "Error".
    if (['l2Open', 'sreOpen', 'l3Swat', 'l3Platform'].includes(key)) {
      if (!vsBuckets) return vsBucketsFailed ? 'Error' : '—';
      return vsBuckets[key] ?? '—';
    }
    return '—';
  };

  // New L1 open cases (New/Open/WIP) whose Salesforce CREATED_DATE is within 24h.
  const l1OpenedLast24h = useMemo(() => {
    if (!l1TicketsReady) return null;
    const cutoff = Date.now() - (24 * 60 * 60 * 1000);
    return (l1Tickets || []).filter((ticket) => {
      if (!isSfHotOpenState(ticket.state)) return false;
      const opened = ticketOpenedAt(ticket);
      return opened && opened.getTime() >= cutoff;
    }).length;
  }, [l1Tickets, l1TicketsReady]);

  const l2Board = useMemo(() => {
    const openTickets = (l2Tickets || []).filter((ticket) => !isAdoDoneOrClosedStatus(ticket.state));
    const counts = countTicketStatuses(openTickets);
    const fromTickets = sumStatusCounts(counts);
    const hero = !l2Loading ? fromTickets : (countOf(l2Stats, 'l2Backlogs') ?? fromTickets);

    return {
      badge: 'L2',
      category: 'Investigation',
      hero,
      heroLabel: 'open Investigation tickets (not Done/Closed)',
      rows: adoStatusRowsFromCounts(counts)
    };
  }, [l2Tickets, l2Loading, l2Stats]);

  const l3Board = useMemo(() => {
    const openTickets = (l3Tickets || []).filter((ticket) => !isAdoDoneOrClosedStatus(ticket.state));
    const counts = countTicketStatuses(openTickets);
    const fromTickets = sumStatusCounts(counts);
    const hero = !l3Loading ? fromTickets : (countOf(l3Stats, 'l3Backlogs') ?? fromTickets);

    return {
      badge: 'L3',
      category: 'Engineering',
      hero,
      heroLabel: 'open Engineer tickets (not Done/Closed)',
      rows: adoStatusRowsFromCounts(counts),
      accent: L3_ACCENT,
      badgeBg: L3_BADGE_BG
    };
  }, [l3Tickets, l3Loading, l3Stats]);

  const overviewL2Board = useMemo(() => {
    const { hero, rows } = linkedAdoBoardFromCases(l1L2AdoTickets, 'l2State', 'No L2 status');
    return {
      badge: 'L2',
      category: 'Investigation',
      hero: l1TicketsReady ? hero : '—',
      heroLabel: 'Salesforce linked to L2 (not Done/Closed)',
      rows
    };
  }, [l1L2AdoTickets, l1TicketsReady]);

  const overviewL3Board = useMemo(() => {
    const { hero, rows } = linkedAdoBoardFromCases(l1L3AdoTickets, 'l3State', 'No L3 status');
    return {
      badge: 'L3',
      category: 'Engineering',
      hero: l1TicketsReady ? hero : '—',
      heroLabel: 'Salesforce linked to L3 (not Done/Closed)',
      rows,
      accent: L3_ACCENT,
      badgeBg: L3_BADGE_BG
    };
  }, [l1L3AdoTickets, l1TicketsReady]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box
        sx={{
          borderRadius: '12px',
          background: `linear-gradient(120deg, ${DEEP_GREEN}, ${GREEN_LIGHT})`,
          color: '#fff',
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ minWidth: 0 }}>
        <Typography
            variant="h5"
            sx={{ fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}
        >
            HighQ Vital Signs
        </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.35, fontWeight: 500 }}
          >
          Operational Excellence
        </Typography>
          <Typography
            sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5, fontSize: '0.75rem', fontWeight: 500 }}
          >
            Last synced: {formatLastSynced(lastLakeSync)}
        </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={l1LakeSyncing ? <CircularProgress size={14} color="inherit" /> : <CloudDownloadIcon />}
          disabled={l1LakeSyncing}
          onClick={() => handleLakehouseSync()}
          sx={{
            textTransform: 'none',
            bgcolor: '#fff',
            color: DEEP_GREEN,
            fontWeight: 600,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
          }}
        >
          Sync
        </Button>
      </Box>

      {l1Error && <Alert severity="error" sx={{ mt: 1.5, mb: 0 }}>{l1Error}</Alert>}
      {l1ImportMessage && (
        <Alert severity={l1ImportError ? 'error' : 'success'} sx={{ mt: 1.5, mb: 0 }}>
          {l1ImportMessage}
        </Alert>
      )}

      <Box sx={{ mt: 2, mb: 2, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(6, minmax(0, 1fr))'
            },
            gap: 1.25,
            alignItems: 'stretch'
          }}
        >
          {TICKET_STAT_TILES.map((stat) => (
            <TicketStatTile
              key={stat.key}
              {...stat}
              selected={stat.key === 'topCustomers'
                ? chainLevel === 'topCustomers'
                : chainLevel === 'tile' && selectedTile === stat.key}
              onClick={() => {
                if (stat.key === 'topCustomers') {
                  setTopCustomersVisited(true);
                  setChainLevel('topCustomers');
                  return;
                }
                setSelectedTile(stat.key);
                setChainLevel('tile');
              }}
              value={tileValue(stat.key)}
              aside={
                stat.key === 'l1Open' && l1OpenedLast24h != null ? (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.25,
                      borderRadius: 2,
                      bgcolor: INCIDENT_BADGE_BG,
                      color: INCIDENT_ACCENT,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {l1OpenedLast24h} new in last 24h
                  </Box>
                ) : stat.key === 'sreOpen' && vsBuckets?.sreMajor > 0 ? (
                  <Box
                    role="status"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: 2,
                      bgcolor: MAJOR_INCIDENT_BG,
                      color: MAJOR_INCIDENT_ACCENT,
                      border: `1px solid ${MAJOR_INCIDENT_ACCENT}`,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <MajorIncidentIcon sx={{ fontSize: 14 }} />
                    {vsBuckets.sreMajor} major {vsBuckets.sreMajor === 1 ? 'incident' : 'incidents'}
                  </Box>
                ) : null
              }
            />
          ))}
        </Box>
      </Box>

      {topCustomersVisited && (
        <Box sx={{ display: chainLevel === 'topCustomers' ? 'block' : 'none' }}>
          <TopCustomersPage OpenCasesTable={TicketBacklogTable} refreshKey={topCustomersRefreshKey} onLoaded={(count, failed, excluded) => {
            setTopCustomersOpen(count);
            setTopCustomersFailed(Boolean(failed));
            setTopCustomersExcluded(excluded || 0);
          }} />
        </Box>
      )}

      {chainLevel === 'all' && (
        <Box>
          {((!l1TicketsReady && !l1Stats && l1Loading) || l2Loading || l3Loading) && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {(l1Error || l2Error || l3Error) && (
            <Alert severity="error" sx={{ maxWidth: 480, mb: 2 }}>
              {/* Show every active error, not just the first truthy one --
                  L1/L2/L3 are independent fetches that can fail independently,
                  and `a || b || c` here would silently drop b/c's message. */}
              {[l1Error, l2Error, l3Error].filter(Boolean).join(' · ')}
            </Alert>
          )}
          <Box
          sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))'
              },
              gap: 1.25,
              alignItems: 'stretch'
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              aria-pressed={chainLevel === 'l1'}
              onClick={() => setChainLevel('l1')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setChainLevel('l1');
                }
              }}
              sx={{ cursor: 'pointer', minWidth: 0, outline: 'none', '&:focus-visible': { outline: `2px solid ${L1_ACCENT}` } }}
            >
              <StatBoardCard {...l1Board} compact />
            </Box>
            <Box
              role="button"
              tabIndex={0}
              aria-pressed={activeSfOverviewTab === 'l2ado'}
              onClick={() => setSfOverviewTab('l2ado')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSfOverviewTab('l2ado');
                }
              }}
              sx={{ cursor: 'pointer', minWidth: 0, outline: 'none', '&:focus-visible': { outline: `2px solid ${L2_ACCENT}` } }}
            >
              <StatBoardCard {...overviewL2Board} compact />
            </Box>
            <Box
              role="button"
              tabIndex={0}
              aria-pressed={activeSfOverviewTab === 'l3ado'}
              onClick={() => setSfOverviewTab('l3ado')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSfOverviewTab('l3ado');
                }
              }}
              sx={{ cursor: 'pointer', minWidth: 0, outline: 'none', '&:focus-visible': { outline: `2px solid ${L3_ACCENT}` } }}
            >
              <StatBoardCard {...overviewL3Board} compact />
            </Box>
            <Box
              role="button"
              tabIndex={0}
              aria-pressed={activeSfOverviewTab === 'snow'}
              onClick={() => setSfOverviewTab('snow')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSfOverviewTab('snow');
                }
              }}
              sx={{ cursor: 'pointer', minWidth: 0, outline: 'none', '&:focus-visible': { outline: `2px solid ${INCIDENT_ACCENT}` } }}
            >
              <StatBoardCard {...overviewSnowBoard} compact />
            </Box>
          </Box>
          <Typography sx={{ color: MUTED, fontSize: '0.8125rem', mt: 2.5, mb: 1 }}>
            Case list by Salesforce link. L2, L3, and ServiceNow here are Salesforce cases with that link. The ServiceNow tab is Ops Center incidents.
          </Typography>
          <OverviewTabStrip
            tabs={SF_OVERVIEW_TABS}
            value={activeSfOverviewTab}
            onChange={setSfOverviewTab}
            ariaLabel="Salesforce case lists"
            sx={{ mb: 2 }}
          />
          <Box sx={{ width: '100%', minWidth: 0 }}>
            {l1TicketsReady && !l1Error && (
              <TicketBacklogTable
                key={activeSfOverviewTab}
                tickets={sfOverviewBoard.tickets}
                title={sfOverviewBoard.title}
                accent={sfOverviewBoard.accent}
                badgeBg={sfOverviewBoard.badgeBg}
                emptyMessage={sfOverviewBoard.emptyMessage}
                showProductFilter
                showChannelFilter={false}
                sfColumnSet={
                  activeSfOverviewTab === 'l2ado'
                    ? 'l2'
                    : activeSfOverviewTab === 'l3ado'
                      ? 'l3'
                      : activeSfOverviewTab === 'snow'
                        ? 'snow'
                        : 'full'
                }
                initialStatusFilter="all"
                initialRowsPerPage={500}
              />
            )}
          </Box>
          <UniqueOpenCountsTable
            tickets={l1Tickets}
            loading={!l1TicketsReady && l1Stats == null}
            sfMonthlySeries={l1Monthly}
            sfMonthlyLoading={l1Loading && !(Array.isArray(l1Monthly) && l1Monthly.length)}
            selectedKey={activeSfOverviewTab}
            openCounts={{
              salesforce: l1Board.hero,
              l2ado: overviewL2Board.hero,
              l3ado: overviewL3Board.hero,
              snow: overviewSnowBoard.hero
            }}
            onSelectChannel={(key) => {
              setSfOverviewTab(key);
            }}
          />
        </Box>
      )}

      {chainLevel === 'tile' && selectedTile === 'l3Swat' && <OpenProductionBugsView l1Tickets={l1Tickets} />}

      {chainLevel === 'tile' && selectedTile === 'l2Open' && <OpenL2IncidentsView />}

      {chainLevel === 'tile' && selectedTile === 'sreOpen' && <SreOpenView />}

      {chainLevel === 'tile' && selectedTile === 'l3Platform' && (
        <PlatformInvestigationTeamTab teams={PLATFORM_INVESTIGATION_TEAMS} vitalSignsLayout />
      )}

      {chainLevel === 'tile' && selectedTile === 'l1Open' && (
      <Box>
        {l1Loading && !l1TicketsReady && l1Stats == null && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!l1Loading && l1Error && <Alert severity="error" sx={{ maxWidth: 360 }}>{l1Error}</Alert>}

        {!l1Error && (l1TicketsReady || l1Stats != null) && (
          <Box>
            {/* Same layout as the L3 tabs: card 4/12, chart 8/12, equal height */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 2, alignItems: 'stretch' }}>
              <L1OverviewCard open={l1Board.hero} newLast24h={l1OpenedLast24h} rows={l1Board.rows} stageRows={l1StageRows} stageAvailable={l1StageAvailable} />
              <AgeBreakdownCard
                tickets={l1OpenTickets}
                selectedBucket={l1AgeBucket}
                onSelectBucket={setL1AgeBucket}
                title="Age of open Salesforce cases"
                accent={L1_ACCENT}
              />
            </Box>
            <Box sx={{ mt: 3, width: '100%', minWidth: 0 }}>
              <TicketBacklogTable
                tickets={l1OpenTickets}
                title="Salesforce · Open cases"
                accent={L1_ACCENT}
                badgeBg={L1_BADGE_BG}
                emptyMessage="No open Salesforce cases."
                showProductFilter
                showChannelFilter={false}
                openBacklogOnly
                ageBucket={l1AgeBucket}
                onClearAge={clearL1Age}
              />
            </Box>
            <Box sx={{ mt: 2.5 }}>
              <BoardOpenedClosedChart
                title="Salesforce"
                subtitle="HighQ"
                series={l1Monthly}
                loading={l1Loading && !(Array.isArray(l1Monthly) && l1Monthly.length)}
                openedColor={L1_ACCENT}
                showReceivedClosed
              />
            </Box>
          </Box>
        )}
      </Box>
      )}

      {chainLevel === 'l2' && (
      <Box>
        {l2Loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!l2Loading && l2Error && <Alert severity="error" sx={{ maxWidth: 360 }}>{l2Error}</Alert>}

        {!l2Loading && !l2Error && (
          <Box>
            <Box sx={boardChartRowSx}>
              <StatBoardCard {...l2Board} />
              <BoardOpenedClosedChart
                title="Investigation"
                subtitle="ADO board"
                series={l2Summary?.monthly || EMPTY_SERIES}
                loading={l2Loading}
                openedColor={L2_ACCENT}
                showReceivedClosed
              />
            </Box>
            <Box sx={{ mt: 3, width: '100%', minWidth: 0 }}>
              <TicketBacklogTable
                tickets={l2Tickets}
                ageBucket={ageBucket}
                onClearAge={clearAge}
              />
            </Box>
            <Box sx={{ mt: 2.5 }}>
              <AgeBreakdownCard
                tickets={l2Tickets}
                selectedBucket={ageBucket}
                onSelectBucket={setAgeBucket}
                title="Age of open Investigation tickets"
                accent={L2_ACCENT}
              />
            </Box>
          </Box>
        )}
      </Box>
      )}

      {chainLevel === 'l3' && (
      <Box>
        {l3Loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!l3Loading && l3Error && <Alert severity="error" sx={{ maxWidth: 360 }}>{l3Error}</Alert>}

        {!l3Loading && !l3Error && (
          <Box>
            <Box sx={boardChartRowSx}>
              <StatBoardCard {...l3Board} />
              <BoardOpenedClosedChart
                title="Engineer"
                subtitle="ADO board"
                series={l3Summary?.monthly || EMPTY_SERIES}
                loading={l3Loading}
                openedColor={L3_ACCENT}
                showReceivedClosed
              />
            </Box>
            <Box sx={{ mt: 3, width: '100%', minWidth: 0 }}>
              <TicketBacklogTable
                tickets={l3Tickets}
                title="Engineer cases"
                accent={L3_ACCENT}
                badgeBg={L3_BADGE_BG}
                emptyMessage="No open Engineer backlog items."
                showType
                ageBucket={l3AgeBucket}
                onClearAge={clearL3Age}
              />
            </Box>
            <Box sx={{ mt: 2.5 }}>
              <AgeBreakdownCard
                tickets={l3Tickets}
                selectedBucket={l3AgeBucket}
                onSelectBucket={setL3AgeBucket}
                title="Age of open Engineer tickets"
                accent={L3_ACCENT}
              />
            </Box>
          </Box>
        )}
      </Box>
      )}

      {chainLevel === 'servicenow' && (
      <Box>
        {snowLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!snowLoading && snowError && <Alert severity="error" sx={{ maxWidth: 480 }}>{snowError}</Alert>}
        {!snowLoading && !snowError && (
          <Box>
            <Box sx={boardChartRowSx}>
              <StatBoardCard
                {...snowBoard}
                showBoardButton
                boardButtonLabel="Open ServiceNow"
              />
              <BoardOpenedClosedChart
                title="ServiceNow"
                subtitle="Ops Center tickets"
                series={snowMonthly}
                loading={snowLoading}
                openedColor={INCIDENT_ACCENT}
                showReceivedClosed
              />
            </Box>
            <Box sx={{ mt: 3, width: '100%', minWidth: 0 }}>
              <TicketBacklogTable
                tickets={portalSnowTickets}
                title="ServiceNow incidents"
                accent={INCIDENT_ACCENT}
                badgeBg={INCIDENT_BADGE_BG}
                emptyMessage="No ServiceNow incidents. Sync them in Admin → Ops Center (same config as Major Incidents)."
                showChannelFilter={false}
                priorityKind="servicenow"
                initialStatusFilter="all"
                initialRowsPerPage={500}
                ageBucket={snowAgeBucket}
                onClearAge={clearSnowAge}
              />
            </Box>
            <Box sx={{ mt: 2.5 }}>
              <AgeBreakdownCard
                tickets={portalSnowOpenTickets}
                selectedBucket={snowAgeBucket}
                onSelectBucket={setSnowAgeBucket}
                title="Age of open ServiceNow incidents"
                accent={INCIDENT_ACCENT}
              />
            </Box>
          </Box>
        )}
      </Box>
      )}
    </Box>
  );
};

const OperationalExcellenceAccessGate = () => {
  if (!hasOperationalExcellenceAccess()) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 520 }}>
          You do not have access to Operational Excellence. Ask an administrator to grant it in Admin → User Management.
        </Alert>
      </Box>
    );
  }
  return <OperationalExcellenceHub />;
};

export default OperationalExcellenceAccessGate;

